import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect, useSelector } from "react-redux";
import { Oval } from "react-loader-spinner";
import { setLoading, setSnackInfo } from "../../redux/actions/appAction";
import { useHistory } from "react-router-dom";
import HttpService from "../../services/httpService";
import CryptoJs from "crypto-js";
import moment from 'moment';
import {
    Button, Paper, Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, IconButton, Chip, Card, CardContent, Grid, Divider, Container, TablePagination,
    Tooltip, Avatar, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions,
    Checkbox, FormControl, InputLabel, Select, MenuItem, Popover, CircularProgress, Menu,
    Tabs, Tab
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import RemoveCircleIcon from "@material-ui/icons/RemoveCircle";
import DownloadIcon from "@material-ui/icons/GetApp";
import HistoryIcon from "@material-ui/icons/History";
import PaymentIcon from "@material-ui/icons/Payment";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import PendingIcon from "@material-ui/icons/Schedule";
import BusinessIcon from "@material-ui/icons/Business";
import SendIcon from "@material-ui/icons/Send";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import SyncIcon from "@material-ui/icons/Sync";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { Close as CloseIcon, Email as EmailIcon } from "@material-ui/icons";
import { isDK } from "../../utils/index";
import * as XLSX from "xlsx";
import { CSVLink } from "react-csv";
import EditIcon from "@material-ui/icons/Edit";
import { Edit3, FileText, Download, Calendar, User, CheckCircle, X, Send } from "lucide-react";

const useStyles = makeStyles((theme) => ({
    root: {
    padding: theme.spacing(3),
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
  },
    // shared look for every table on the page (header / body cells / zebra rows)
    tableWrap: {
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        "& .MuiTableCell-head": {
            fontSize: "0.78rem !important",
            letterSpacing: 0.4,
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            padding: "12px 12px",
            fontWeight: "700 !important",
        },
        "& .MuiTableCell-body": {
            fontSize: "0.85rem",
            padding: "10px 12px",
            borderBottom: "1px solid #eef2f7",
        },
        "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
            backgroundColor: "#f8fafc",
        },
        "& .MuiTableBody-root .MuiTableRow-root:hover": {
            backgroundColor: "#eff6ff",
        },
    },

    tableContainer: {
        borderRadius: 12,
        overflow: "hidden",
        marginTop: theme.spacing(3),
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    },
    tableRow: {
        transition: "all 0.2s ease",
    },
    filterContainer: {
        padding: theme.spacing(2),
        backgroundColor: "#fff",
        borderRadius: 14,
        boxShadow: "0 4px 20px rgba(15,23,42,0.07)",
        border: "1px solid #e2e8f0",
        marginBottom: theme.spacing(3),
    },
    summaryCard: {
        padding: theme.spacing(1),
        borderRadius: 14,
        backgroundColor: "#fff",
        border: "1px solid #e2e8f0",
        boxShadow: "0 4px 20px rgba(15,23,42,0.07)",
        transition: "all 0.3s ease",
        "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
        },
    },
    amountText: {
        fontWeight: 700,
        color: "#059669",
    },
    statLabel: {
        color: "#64748b",
        fontSize: "0.875rem",
        fontWeight: 500,
    },
    statValue: {
        fontWeight: 700,
        fontSize: "1.5rem",
        marginTop: theme.spacing(0.5),
    },
    filterButton: {
        backgroundColor: "#2563eb",
        color: "#fff",
        textTransform: "none",
        borderRadius: 8,
        padding: theme.spacing(1, 3),
        "&:hover": {
            backgroundColor: "#1d4ed8",
        },
    },
    clearButton: {
        backgroundColor: "#fff",
        color: "#dc2626",
        border: "1px solid #dc2626",
        textTransform: "none",
        borderRadius: 8,
        padding: theme.spacing(1, 3),
        marginLeft : 2,
        boxShadow: "none",
        "&:hover": {
            backgroundColor: "#fef2f2",
        },
    },
    processButton: {
        backgroundColor: "#2563eb",
        color: "#fff",
        textTransform: "none",
        borderRadius: 8,
        padding: theme.spacing(1, 3),
        "&:hover": {
            backgroundColor: "#1d4ed8",
        },
        "&.Mui-disabled": {
            backgroundColor: "#94a3b8",
            color: "#fff",
        },
    },
    statusChip: {
        "&.pending": {
            backgroundColor: "#fef9c3",
            color: "#a16207",
        },
        "&.approved": {
            backgroundColor: "#dcfce7",
            color: "#16a34a",
        },
        "&.rejected": {
            backgroundColor: "#fecaca",
            color: "#dc2626",
        },
    },
}));

// ─── Payment Type Constants ─────────────────────────────────────────────────
const PAYMENT_TYPE_LABELS = {
    rent: "Rent",
    housekeeping: "Housekeeping",
    stationary: "Stationary",
    porter: "Porter",
    ccd: "CCD",
    drinking_water: "Drinking Water",
    marketing: "Marketing",
    branding: "Branding",
    others: "Others",
};

const getBankTransactionTypeCode = (row) => {
    const ifsc = row?.ifsc || "";
    const amount = parseFloat(row?.amount || 0);
    if (ifsc.includes("HDFC")) return "I";
    return amount < 200000 ? "N" : "R";
};

// Uniform "-" fallback for any missing/blank cell value.
const displayOrDash = (value) => (value === null || value === undefined || value === "" ? "-" : value);

const MiscpaymentMasterFinanceDashboard = (props) => {
    const { userDetails } = useSelector((state) => state.user);
    const classes = useStyles();
    const history = useHistory();
    const ref = useRef(null);
    const todayDate = moment().format("DD/MM/YYYY");
    const paymentIdCounterRef = useRef(0);
    const generateUniquePaymentId = (prefix) => {
        const now = new Date();
        const formatted = now.toISOString().split('.')[0].replace(/[-:T]/g, '');
        paymentIdCounterRef.current += 1;
        return `${prefix}${formatted}${paymentIdCounterRef.current}`;
    };

    // ─── State Initialization ──────────────────────────────────────────────
    const [otpSend, setOtpSend] = useState(false);
    const [otpResponse, setOtpResponse] = useState([]);
    const [otpVerified, setOtpVerified] = useState(false);
    const [paymentData, setPaymentData] = useState([]);
    const [selectedPaymentData, setSelectedPaymentData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [approvalLoading, setApprovalLoading] = useState(false);
    const [dkModalOpen, setDkModalOpen] = useState(false);
    const [paymentmodalOpen, setPaymentmodalOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [processingProgress, setProcessingProgress] = useState(0);
    const [processingMessage, setProcessingMessage] = useState('');
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [canCloseModal, setCanCloseModal] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [loadingg, setLoadingState] = useState(true);
    const [uploadCooldown, setUploadCooldown] = useState(false);
    const [cooldownTimeLeft, setCooldownTimeLeft] = useState(0);
    const [cooldownInterval, setCooldownInterval] = useState(null);
    const [excelLink, setExcelLink] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [totalDocs, setTotalDocs] = useState(0);
    const [activeTab, setActiveTab] = useState('misc_ec');
    const [tabPage, setTabPage] = useState(0);
    const [tabRowsPerPage, setTabRowsPerPage] = useState(100);
    const [referralTabRows, setReferralTabRows] = useState([]);
    const [referralTabTotal, setReferralTabTotal] = useState(0);
    const [referralTabLoading, setReferralTabLoading] = useState(false);
    const [refundTabRows, setRefundTabRows] = useState([]);
    const [refundTabTotal, setRefundTabTotal] = useState(0);
    const [refundTabLoading, setRefundTabLoading] = useState(false);
    const [siteVisitTabRows, setSiteVisitTabRows] = useState([]);
    const [siteVisitTabTotal, setSiteVisitTabTotal] = useState(0);
    const [siteVisitTabLoading, setSiteVisitTabLoading] = useState(false);
    const [channelPartnerTabRows, setChannelPartnerTabRows] = useState([]);
    const [channelPartnerTabTotal, setChannelPartnerTabTotal] = useState(0);
    const [channelPartnerTabLoading, setChannelPartnerTabLoading] = useState(false);

    // ─── Filter State ──────────────────────────────────────────────────────
    const [paymentTypeFilter, setPaymentTypeFilter] = useState("all");
    const [experienceCenterFilter, setExperienceCenterFilter] = useState("all");
    // Client-side multi-select filters (applied over the currently loaded page of rows).
    const [vendorFilter, setVendorFilter] = useState([]);
    const [cityFilter, setCityFilter] = useState([]);
    const [scopeFilter, setScopeFilter] = useState([]); // service/expense type — same underlying field as paymentType
    const [allExperienceCenters, setAllExperienceCenters] = useState([]);
    const [dateFilter, setDateFilter] = useState({
        startDate: "",
        endDate: ""
    });

    // ─── Sync State ────────────────────────────────────────────────────────
    const [syncType, setSyncType] = useState('ec_payment');
    const [syncTypeMenuAnchor, setSyncTypeMenuAnchor] = useState(null);

    const [selectedReferralKeys, setSelectedReferralKeys] = useState([]);
    const [selectedRefundKeys, setSelectedRefundKeys] = useState([]);
    const [selectedSiteVisitKeys, setSelectedSiteVisitKeys] = useState([]);
    const [selectedChannelPartnerKeys, setSelectedChannelPartnerKeys] = useState([]);

    const [syncModalOpen, setSyncModalOpen] = useState(false);
    const [snorkelLogs, setSnorkelLogs] = useState([]);
    const [syncLogsLoading, setSyncLogsLoading] = useState(false);
    const [selectedSyncLog, setSelectedSyncLog] = useState(null);
    const [syncRunning, setSyncRunning] = useState(false);
    const [syncResultModal, setSyncResultModal] = useState(false);
    const [syncResult, setSyncResult] = useState(null);

    // ── Edit Amount (revise before sending to bank) ──────────────────────
    const [reviseDialogOpen, setReviseDialogOpen] = useState(false);
    const [reviseRow, setReviseRow] = useState(null);
    const [reviseAmount, setReviseAmount] = useState("");
    const [reviseType, setReviseType] = useState("none");
    const [revisePostponedNote, setRevisePostponedNote] = useState("");
    const [revisePenaltyAmount, setRevisePenaltyAmount] = useState("");
    const [revising, setRevising] = useState(false);

    const handleOpenReviseDialog = (row) => {
        setReviseRow(row);
        setReviseAmount(row.revisedAmount ?? row.amount);
        setReviseType(row.revisionType || "none");
        setRevisePostponedNote(row.postponedNote || "");
        setRevisePenaltyAmount(row.penaltyAmount || "");
        setReviseDialogOpen(true);
    };

    const handleSubmitRevise = async () => {
        if (!reviseRow) return;
        setRevising(true);
        try {
            const res = await HttpService.revisePaymentForMiscPayment(reviseRow.paymentId, {
                revised_amount: reviseAmount,
                revision_type: reviseType,
                postponed_note: reviseType === "postponed" ? revisePostponedNote : undefined,
                penalty_amount: reviseType === "penalty" ? revisePenaltyAmount : undefined,
            });
            if (res?.data?.success) {
                props.setSnackInfo("Payment revised successfully", "success");
                setReviseDialogOpen(false);
                generateVendorPaymentSnorkelHDFC(page, rowsPerPage, dateFilter.startDate, dateFilter.endDate);
            }
        } catch (err) {
            props.setSnackInfo(err?.response?.data?.message || "Failed to revise payment", "error");
        } finally {
            setRevising(false);
        }
    };

    // ── Last 3 months' amount history (shown from the Amount column) ────
    const [historyAnchorEl, setHistoryAnchorEl] = useState(null);
    const [historyRows, setHistoryRows] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const handleShowAmountHistory = async (event, row) => {
        setHistoryAnchorEl(event.currentTarget);
        setHistoryLoading(true);
        setHistoryRows([]);
        try {
            const linkedOwnerId = row.linkedOwner?._id;
            const linkedProviderId = row.linkedServiceProvider?._id;
            if (!linkedOwnerId && !linkedProviderId) return;
            const res = await HttpService.getPaymentHistoryForMiscPayment({
                linked_owner: linkedOwnerId || undefined,
                linked_service_provider: linkedProviderId || undefined,
                payment_type: row.paymentType,
                status: "approved",
                limit: 3,
            });
            const rows = (res?.data?.data?.payments || []).filter((p) => p._id !== row.paymentId);
            setHistoryRows(rows);
        } catch (err) {
            setHistoryRows([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    const pendingS3LinkRef = useRef(null);

    // ─── Excel Headers ──────────────────────────────────────────────────────
    const excelHeaders = [
        "SL No", "Type", "Vendor Name", "Order ID", "Account No",
        "Date", "Amount", "IFSC", "Bank", "Email"
    ];

    const csvHeaders = [
        { label: "SL No", key: "serialNo" },
        { label: "Bank Type", key: "bankTxnType" },
        { label: "Type", key: "paymentType" },
        { label: "Vendor Name", key: "vendorName" },
        { label: "Order ID", key: "orderId" },
        { label: "Account No", key: "accountNumber" },
        { label: "Date", key: "date" },
        { label: "Amount", key: "amount" },
        { label: "Account Holder", key: "accountHolder" },
        { label: "IFSC", key: "ifsc" },
        { label: "Bank", key: "bankName" },
        { label: "Email", key: "email" },
    ];

    const referralCsvHeaders = [
        { label: "SL No", key: "serialNo" },
        { label: "Bank Type", key: "bankTxnType" },
        { label: "Unq Lead No", key: "uniqueLeadNumber" },
        { label: "Referred By", key: "referedBy" },
        { label: "Referred By Unq Lead No", key: "referrerUniqueLeadNumber" },
        { label: "Referred To", key: "referedTo" },
        { label: "Referral Type", key: "referalType" },
        { label: "Stage", key: "stage" },
        { label: "Payment Type", key: "paymentType" },
        { label: "Amount", key: "amount" },
        { label: "Approved Date", key: "approvedDate" },
        { label: "Account No", key: "accountNumber" },
        { label: "Account Holder", key: "payeeName" },
        { label: "IFSC", key: "ifsc" },
        { label: "Bank", key: "bankName" },
        { label: "Email", key: "email" },
    ];

    const refundCsvHeaders = [
        { label: "SL No", key: "serialNo" },
        { label: "Bank Type", key: "bankTxnType" },
        { label: "Unq Lead No", key: "uniqueLeadNumber" },
        { label: "Customer Name", key: "customerName" },
        { label: "Amount", key: "amount" },
        { label: "Refund Raised Date", key: "refundRaiseDate" },
        { label: "Account No", key: "accountNumber" },
        { label: "Account Holder", key: "payeeName" },
        { label: "IFSC", key: "ifsc" },
        { label: "Bank", key: "bankName" },
        { label: "Email", key: "email" },
    ];

    const siteVisitCsvHeaders = [
        { label: "SL No", key: "serialNo" },
        { label: "Bank Type", key: "bankTxnType" },
        { label: "Source Lead No", key: "uniqueLeadNumber" },
        { label: "Referrer Lead No", key: "referrerUniqueLeadNumber" },
        { label: "Referrer Name", key: "referrerName" },
        { label: "Amount", key: "amount" },
        { label: "Approved Date", key: "approvedDate" },
        { label: "Account No", key: "accountNumber" },
        { label: "Account Holder", key: "payeeName" },
        { label: "IFSC", key: "ifsc" },
        { label: "Bank", key: "bankName" },
        { label: "Email", key: "email" },
    ];

    const channelPartnerCsvHeaders = [
        { label: "SL No", key: "serialNo" },
        { label: "Bank Type", key: "bankTxnType" },
        { label: "Payout Code", key: "payoutCode" },
        { label: "CP Code", key: "cpCode" },
        { label: "Channel Partner Name", key: "channelPartnerName" },
        { label: "Lead No", key: "uniqueLeadNumber" },
        { label: "Amount", key: "amount" },
        { label: "Approved Date", key: "approvedDate" },
        { label: "Account No", key: "accountNumber" },
        { label: "Account Holder", key: "payeeName" },
        { label: "IFSC", key: "ifsc" },
        { label: "Bank", key: "bankName" },
        { label: "Email", key: "email" },
    ];

    // ─── Fetch Experience Centers ──────────────────────────────────────────
    const fetchExperienceCenters = useCallback(async () => {
        try {
            props.setLoading(true);
            const res = await HttpService.getAllExperienceCenter();
            setAllExperienceCenters(res?.data?.data || []);
        } catch (err) {
            props.setSnackInfo("Failed to load Experience Centres", "error");
        } finally {
            props.setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchExperienceCenters();
    }, []);

    // ─── Fetch ERP Payment Requests ──────────────────────────────────────
    const generateVendorPaymentSnorkelHDFC = async (pageNumber = 0, limit = 100, startDateParam, endDateParam) => {
        setLoading(true);
        try {
            props.setLoading(true);

            const params = {
                page: pageNumber + 1,
                limit: limit,
            };

            if (paymentTypeFilter !== "all") {
                params.payment_type = paymentTypeFilter;
            }

            if (experienceCenterFilter !== "all") {
                params.experience_center_id = experienceCenterFilter;
            }

            if (startDateParam) {
                params.start_date = startDateParam;
            }

            if (endDateParam) {
                params.end_date = endDateParam;
            }

            console.log("Fetching with params:", params);

            const result = await HttpService.getERPRequestsForFinance(params);

            console.log("API Response:", result);

            if (result?.data?.success) {
                // The data is nested inside data.data
                const responseData = result.data.data || {};
                const payments = responseData.data || [];
                setPaymentData(payments);
                setTotalDocs(responseData.totalDocs || 0);
                props.setSnackInfo("Data loaded successfully", "success");
            } else {
                setPaymentData([]);
                setTotalDocs(0);
                props.setSnackInfo(result?.data?.message || "No data found", "info");
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            setPaymentData([]);
            setTotalDocs(0);
            props.setSnackInfo(
                error?.response?.data?.message ?? "Something went wrong",
                "error"
            );
        } finally {
            setLoading(false);
            setLoadingState(false);
            props.setLoading(false);
        }
    };

    // ─── Fetch Referral / Refund tab data (main table, not the sync dialog) ─
    const fetchReferralTabData = async (pageNumber = 0, limit = 100, startDateParam, endDateParam) => {
        setReferralTabLoading(true);
        try {
            props.setLoading(true);
            const params = { page: pageNumber + 1, limit };
            if (experienceCenterFilter !== "all") params.experience_center_id = experienceCenterFilter;
            const startDate = startDateParam !== undefined ? startDateParam : dateFilter.startDate;
            const endDate = endDateParam !== undefined ? endDateParam : dateFilter.endDate;
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;

            const result = await HttpService.getPendingReferralPayments(params);
            if (result?.data?.success) {
                const responseData = result.data.data || {};
                setReferralTabRows(responseData.data || []);
                setReferralTabTotal(responseData.totalDocs || 0);
            } else {
                setReferralTabRows([]);
                setReferralTabTotal(0);
            }
        } catch (error) {
            setReferralTabRows([]);
            setReferralTabTotal(0);
            props.setSnackInfo(error?.response?.data?.message ?? "Failed to load referral payments", "error");
        } finally {
            setReferralTabLoading(false);
            props.setLoading(false);
        }
    };

    const fetchRefundTabData = async (pageNumber = 0, limit = 100, startDateParam, endDateParam) => {
        setRefundTabLoading(true);
        try {
            props.setLoading(true);
            const params = { page: pageNumber + 1, limit };
            if (experienceCenterFilter !== "all") params.experience_center_id = experienceCenterFilter;
            const startDate = startDateParam !== undefined ? startDateParam : dateFilter.startDate;
            const endDate = endDateParam !== undefined ? endDateParam : dateFilter.endDate;
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;

            const result = await HttpService.getPendingRefundPayments(params);
            if (result?.data?.success) {
                const responseData = result.data.data || {};
                setRefundTabRows(responseData.data || []);
                setRefundTabTotal(responseData.totalDocs || 0);
            } else {
                setRefundTabRows([]);
                setRefundTabTotal(0);
            }
        } catch (error) {
            setRefundTabRows([]);
            setRefundTabTotal(0);
            props.setSnackInfo(error?.response?.data?.message ?? "Failed to load refund payments", "error");
        } finally {
            setRefundTabLoading(false);
            props.setLoading(false);
        }
    };
    const fetchSiteVisitTabData = async (pageNumber = 0, limit = 100, startDateParam, endDateParam) => {
        setSiteVisitTabLoading(true);
        try {
            props.setLoading(true);
            const params = { page: pageNumber + 1, limit };
            const startDate = startDateParam !== undefined ? startDateParam : dateFilter.startDate;
            const endDate = endDateParam !== undefined ? endDateParam : dateFilter.endDate;
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;

            const result = await HttpService.getPendingSiteVisitPayments(params);
            if (result?.data?.success) {
                const responseData = result.data.data || {};
                setSiteVisitTabRows(responseData.data || []);
                setSiteVisitTabTotal(responseData.totalDocs || 0);
            } else {
                setSiteVisitTabRows([]);
                setSiteVisitTabTotal(0);
            }
        } catch (error) {
            setSiteVisitTabRows([]);
            setSiteVisitTabTotal(0);
            props.setSnackInfo(error?.response?.data?.message ?? "Failed to load site visit payments", "error");
        } finally {
            setSiteVisitTabLoading(false);
            props.setLoading(false);
        }
    };

    const fetchChannelPartnerTabData = async (pageNumber = 0, limit = 100, startDateParam, endDateParam) => {
        setChannelPartnerTabLoading(true);
        try {
            props.setLoading(true);
            const params = { page: pageNumber + 1, limit };
            const startDate = startDateParam !== undefined ? startDateParam : dateFilter.startDate;
            const endDate = endDateParam !== undefined ? endDateParam : dateFilter.endDate;
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;

            const result = await HttpService.getPendingChannelPartnerPayouts(params);
            if (result?.data?.success) {
                const responseData = result.data.data || {};
                setChannelPartnerTabRows(responseData.data || []);
                setChannelPartnerTabTotal(responseData.totalDocs || 0);
            } else {
                setChannelPartnerTabRows([]);
                setChannelPartnerTabTotal(0);
            }
        } catch (error) {
            setChannelPartnerTabRows([]);
            setChannelPartnerTabTotal(0);
            props.setSnackInfo(error?.response?.data?.message ?? "Failed to load Channel Partner payouts", "error");
        } finally {
            setChannelPartnerTabLoading(false);
            props.setLoading(false);
        }
    };

    const getUploadLogsApi = (type) => ({
        ec_payment: HttpService.getSnorkelUploadLogsEC,
        referral: HttpService.getReferralUploadLogs,
        refund: HttpService.getRefundUploadLogs,
        siteVisit: HttpService.getSiteVisitUploadLogs,
        channelPartner: HttpService.getChannelPartnerUploadLogs,
    }[type || syncType]);
    const saveUploadLogApi = (type) => ({
        ec_payment: HttpService.saveSnorkelUploadLogEC,
        referral: HttpService.saveReferralUploadLog,
        refund: HttpService.saveRefundUploadLog,
        siteVisit: HttpService.saveSiteVisitUploadLog,
        channelPartner: HttpService.saveChannelPartnerUploadLog,
    }[type || syncType]);
    const syncApi = (type) => ({
        ec_payment: HttpService.syncSnorkelPaymentsEC,
        referral: HttpService.syncReferralPayments,
        refund: HttpService.syncRefundPayments,
        siteVisit: HttpService.syncSiteVisitPayments,
        channelPartner: HttpService.syncChannelPartnerPayouts,
    }[type || syncType]);

    const uploadToS3Api = (type) => (formData) => HttpService.uploadToS3ForSnorkelPayment(formData, type || syncType);

    // ─── Fetch Snorkel Logs ────────────────────────────────────────────────
    const fetchSnorkelLogs = async (typeOverride) => {
        setSyncLogsLoading(true);
        try {
            const res = await getUploadLogsApi(typeOverride)();
            if (res?.data?.success) {
                setSnorkelLogs(res.data.data || []);
            }
        } catch (err) {
            console.error("Failed to fetch snorkel logs:", err);
        } finally {
            setSyncLogsLoading(false);
        }
    };

    // ─── Save Snorkel Upload Log ────────────────────────────────────────────
    const saveSnorkelUploadLog = async (s3Location, fileName) => {
        try {
            await saveUploadLogApi()({ s3Location, fileName });
        } catch (err) {
            console.error("Failed to save snorkel upload log:", err);
        }
    };

    // ─── Run Sync ────────────────────────────────────────────────────────────
    const handleRunSync = async () => {
        if (!selectedSyncLog) {
            props.setSnackInfo("Please select an upload log to sync", "warning");
            return;
        }
        setSyncRunning(true);
        try {
            const res = await syncApi()({
                logId: selectedSyncLog._id,
                s3Location: selectedSyncLog.s3Location
            });
            setSyncResult({ ...res.data.data, message: res.data.message });
            setSyncResultModal(true);
            if (syncType === 'ec_payment') {
                generateVendorPaymentSnorkelHDFC(page, rowsPerPage, dateFilter.startDate, dateFilter.endDate);
            } else if (syncType === 'referral') {
                fetchReferralTabData(tabPage, tabRowsPerPage);
            } else if (syncType === 'refund') {
                fetchRefundTabData(tabPage, tabRowsPerPage);
            } else if (syncType === 'siteVisit') {
                fetchSiteVisitTabData(tabPage, tabRowsPerPage);
            } else if (syncType === 'channelPartner') {
                fetchChannelPartnerTabData(tabPage, tabRowsPerPage);
            }
            props.setSnackInfo("Sync completed successfully!", "success");
        } catch (err) {
            props.setSnackInfo(err?.response?.data?.message ?? "Sync failed. Try again.", "error");
        } finally {
            setSyncRunning(false);
        }
    };

    // ─── Generate and Upload Excel ──────────────────────────────────────────
    const generateAndUploadExcel = async (selectedData) => {
        try {
            const maxIdsLength = Math.max(
                ...selectedData.map(item =>
                    (item.approvedPaymentIds || "").split("/").filter(Boolean).length
                )
            );

            const formattedData = selectedData.map((item, index) => {
                const ids = (item.approvedPaymentIds || "").split("/");
                let dynamicCols = {};
                for (let i = 0; i < maxIdsLength; i++) {
                    dynamicCols[`Col${i + 1}`] = ids[i] || "";
                }

                return {
                    "SL No": index + 1,
                    "Type": item.paymentType,
                    "Vendor Name": item.vendorName,
                    "Order ID": item.orderId,
                    "Account No": item.accountNumber,
                    "Date": item.date,
                    "Amount": item.amount,
                    "IFSC": item.ifsc,
                    "Bank": item.bankName,
                    "Email": item.email,
                    ...dynamicCols
                };
            });

            const totalAmount = selectedData.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.json_to_sheet(formattedData, {
                header: excelHeaders
            });

            XLSX.utils.book_append_sheet(workbook, worksheet, 'Vendor Payments');

            const excelBuffer = XLSX.write(workbook, {
                type: 'array',
                bookType: 'xlsx'
            });

            const blob = new Blob([excelBuffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const formData = new FormData();
            const filename = `VendorPayments_${Date.now()}.xlsx`;
            formData.append('file', blob, filename);

            const response = await HttpService.uploadFile(formData);
            const s3Link = response.data.s3Link || response.data;
            return { s3Link, totalAmount };

        } catch (error) {
            console.error('Error generating/uploading Excel:', error);
            throw error;
        }
    };

    // ─── Send OTP ────────────────────────────────────────────────────────────
    const setOTPMessage = async (e) => {
        if (uploadCooldown) {
            props.setSnackInfo(
                `Please wait ${formatTimeRemaining(cooldownTimeLeft)} before uploading another file`,
                "warning"
            );
            return;
        }
        setLoading(true);
        try {
            const { s3Link, totalAmount } = await generateAndUploadExcel(selectedPaymentData);
            setExcelLink(s3Link);
            pendingS3LinkRef.current = s3Link;

            const obj = {
                email: isDK?.email,
                excellink: s3Link,
                total_so_amt: totalAmount.toFixed(2)
            };

            const response = await HttpService.sendOtpMessageToAnyEmail(obj);
            setOtpSend(true);

            var bytes = CryptoJs.AES.decrypt(response.data.otp, "decorpot123");
            var decrypted_otpNumber = bytes.toString(CryptoJs.enc.Utf8);
            setOtpResponse({ otp: Number(decrypted_otpNumber) });
            ref.current?.scrollIntoView({ behavior: "smooth" });
            let focusTextFieldOtp = document.getElementById("outlined-basic1-otp");
            focusTextFieldOtp.focus();
        } catch (error) {
            props.setSnackInfo(error.response.data.msg, "error");
        }
        setLoading(false);
    };

    // ─── Verify OTP ──────────────────────────────────────────────────────────
    const verifyOTP = (e) => {
        let n = new String(e.target.value);
        if (n.length === 6 && otpResponse.otp === +e.target.value) {
            setOtpVerified(true);
        } else {
            setOtpVerified(false);
        }
    };

    // ─── Final Approval Flow ────────────────────────────────────────────────
    const FinalApprovalFlowByDKFunction = async () => {
        if (selectedPaymentData.length === 0) {
            props.setSnackInfo("No payment data to send for approval", "error");
            return;
        }

        try {
            setApprovalLoading(true);
            setShowProgressModal(true);
            setCanCloseModal(false);
            setProcessingMessage("Please wait processing is going on......");
            setProcessingProgress(10);

            const formatedTxtData = await createTxtData(selectedPaymentData);

            if (!formatedTxtData) {
                props.setSnackInfo("Failed to generate TXT file", "error");
                return;
            }

            const fileName = generateFileName(null);
            const formData = new FormData();
            formData.append("file", formatedTxtData, fileName);

            setProcessingMessage("Uploading file please wait...");
            setProcessingProgress(50);

            const uploadResponse = await uploadToS3Api()(formData);

            if (!uploadResponse?.data?.success) {
                props.setSnackInfo("Failed to upload file to S3", "error");
                return;
            }

            const s3Location = uploadResponse?.data?.s3Location;

            if (pendingS3LinkRef.current) {
                await saveSnorkelUploadLog(pendingS3LinkRef.current, fileName);
                pendingS3LinkRef.current = null;
                fetchSnorkelLogs();
            }

            startUploadCooldown();
            setProcessingProgress(100);
            setProcessingMessage("File uploaded to Snorkel successfully!");
            setCanCloseModal(true);
            props.setSnackInfo("File sent to Snorkel successfully!", "success");
            setSelectedPaymentData([]);
            if (syncType === 'referral') {
                setSelectedReferralKeys([]);
            } else if (syncType === 'refund') {
                setSelectedRefundKeys([]);
            } else if (syncType === 'siteVisit') {
                setSelectedSiteVisitKeys([]);
            } else if (syncType === 'channelPartner') {
                setSelectedChannelPartnerKeys([]);
            } else {
                setSelectedRows([]);
                generateVendorPaymentSnorkelHDFC(page, rowsPerPage, dateFilter.startDate, dateFilter.endDate);
            }

        } catch (error) {
            console.error(error);
            setProcessingMessage("Processing failed!");
            setCanCloseModal(true);
            props.setSnackInfo(
                error?.response?.data?.message ?? "Failed to send. Try again after some time.",
                "error"
            );
        } finally {
            setApprovalLoading(false);
        }
    };
    // HDFC caps the beneficiary name at 30 chars, and the file is comma
    // separated, so commas in the name would shift every column after it.
    const formatBeneficiaryName = (name) =>
        (name || "").toString().replace(/,/g, " ").trim().slice(0, 30);

    // ─── Helper Functions ────────────────────────────────────────────────────
    const createTxtData = async (data) => {
        if (!data || data.length === 0) {
            props.setSnackInfo("No data available to export", "warning");
            return null;
        }
        try {
            const todayForBank = moment().format("DD/MM/YYYY");
            const lines = data.map((row) => {
                const line = [
                    getBankTransactionTypeCode(row),
                    "",
                    row.accountNumber || "",
                    parseFloat(row?.amount || 0).toFixed(2),
                    // Beneficiary name for the bank must be the bank's own Account
                    // Holder name — vendorName is a display label and can carry
                    // digits, commas, etc. which corrupt this comma-separated file.
                    formatBeneficiaryName(row?.accountHolder || row?.vendorName),
                    "", "", "", "", "", "", "", "", "",
                    row?.OrderNumber,
                    row?.AllPaymentId,
                    "", "", "", "", "", "",
                    todayForBank,
                    "",
                    row?.ifsc || "",
                    row?.bankName || "",
                    "",
                    row?.email || ""
                ].join(",");
                return line;
            });
            const content = lines.join("\n");
            return new Blob([content], { type: "text/plain" });
        } catch (error) {
            console.error("Error generating TXT file:", error);
            return null;
        }
    };

    const generateFileName = (file,Domain = "SAMIKACX" ,clientCode = '5620') => {
        const now = moment();
        const dayMonth = now.format('DDMM');
        const msOfDay = now.diff(now.clone().startOf('day'), 'milliseconds');
        const interval = 3 * 60 * 1000;
        const slotNumber = Math.floor(msOfDay / interval) % 1000;
        const srlNumber = slotNumber.toString().padStart(3, '0');
        return `${Domain}_${clientCode}_${clientCode}${dayMonth}.${srlNumber}.txt`;
    };
    // prod
    // File Naming Convention: DomainID_ClientCode_ClientCodeDDMM.3DigitSerialNumber  
    // Domain: SAMIKACX  
    // Client Code: 5620 
    // Example: SAMIKACX_5620_56200108.437  

    // test/staging
    // File Naming Convention: DomainID_ClientCode_ClientCodeDDMM.3DigitSerialNumber  
    // Domain: H2HCBX  
    // Client Code: SRBI 
    // Example: H2HCBX_SRBI_SRBI0108.437 

    const startUploadCooldown = () => {
        const COOLDOWN_DURATION = 4 * 60 * 1000;
        const cooldownEndTime = Date.now() + COOLDOWN_DURATION;
        localStorage.setItem('uploadCooldownEnd', cooldownEndTime.toString());

        const COOLDOWN_SECONDS = 240;
        setUploadCooldown(true);
        setCooldownTimeLeft(COOLDOWN_SECONDS);

        const interval = setInterval(() => {
            setCooldownTimeLeft(prevTime => {
                if (prevTime <= 1) {
                    setUploadCooldown(false);
                    clearInterval(interval);
                    setCooldownInterval(null);
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);

        setCooldownInterval(interval);

        setTimeout(() => {
            setUploadCooldown(false);
            setCooldownTimeLeft(0);
            if (interval) {
                clearInterval(interval);
                setCooldownInterval(null);
            }
        }, COOLDOWN_DURATION);
    };

    const formatTimeRemaining = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    // ─── Handlers ────────────────────────────────────────────────────────────
    const handleDateChange = (e) => {
        setDateFilter({
            ...dateFilter,
            [e.target.name]: e.target.value
        });
    };

    const handleDateFilter = () => {
        if (!dateFilter.startDate || !dateFilter.endDate) {
            return props.setSnackInfo("Start and End Date is needed", "warning");
        }
        if (activeTab === 'referral') {
            setTabPage(0);
            fetchReferralTabData(0, tabRowsPerPage, dateFilter.startDate, dateFilter.endDate);
        } else if (activeTab === 'refund') {
            setTabPage(0);
            fetchRefundTabData(0, tabRowsPerPage, dateFilter.startDate, dateFilter.endDate);
        } else {
            setPage(0);
            generateVendorPaymentSnorkelHDFC(0, rowsPerPage, dateFilter.startDate, dateFilter.endDate);
        }
    };

    const handleDateFilterClear = () => {
        setDateFilter({ startDate: "", endDate: "" });
        setPaymentTypeFilter("all");
        setExperienceCenterFilter("all");
        if (activeTab === 'referral') {
            setTabPage(0);
            fetchReferralTabData(0, tabRowsPerPage, "", "");
        } else if (activeTab === 'refund') {
            setTabPage(0);
            fetchRefundTabData(0, tabRowsPerPage, "", "");
        } else {
            setPage(0);
            generateVendorPaymentSnorkelHDFC(0, rowsPerPage, "", "");
        }
    };

    const handleRowSelect = (serialNo) => {
        setSelectedRows(prevSelected => {
            if (prevSelected.includes(serialNo)) {
                return prevSelected.filter(id => id !== serialNo);
            } else {
                return [...prevSelected, serialNo];
            }
        });
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const allSerialNos = paymentData.map(row => row.serialNo);
            setSelectedRows(allSerialNos);
        } else {
            setSelectedRows([]);
        }
    };

    const handleProceedDK = async () => {
        if (selectedRows.length === 0) {
            props.setSnackInfo("Please select at least one row to proceed", "warning");
            return;
        }
        if (selectedRows.length > 500) {
            props.setSnackInfo("Can not select more than 500 items at once", "warning");
            return;
        }

        // `amount` here must be the actual amount going to the bank: whatever
        // finance revised it to (or net payable — base + GST − TDS — if they
        // didn't), minus any penalty deduction. This is what the Excel export,
        // OTP total, and the HDFC bank file all key off of downstream.
        const selectedData = paymentData
            .filter(row => selectedRows.includes(row.serialNo))
            .map(row => ({ ...row, amount: row.paidAmount, AllPaymentId: generateUniquePaymentId('DPECP') }));

        setSyncType('ec_payment');
        setSelectedPaymentData(selectedData);
        setDkModalOpen(true);
    };

    const openSyncModal = (type) => {
        setSyncTypeMenuAnchor(null);
        setSyncType(type);
        setSelectedSyncLog(null);
        setSnorkelLogs([]);
        setSyncModalOpen(true);
        fetchSnorkelLogs(type);
    };

    // ── Referral / Refund tab row selection helpers ──────────────────────
    const referralRowKey = (row) => `${row.referralId}:${row.paymentType}`;
    const refundRowKey = (row) => `${row.leadId}:${row.refundEntryId}`;

    const handleReferralRowSelect = (key) => {
        setSelectedReferralKeys((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };
    const handleSelectAllReferral = (event) => {
        setSelectedReferralKeys(event.target.checked ? referralTabRows.map(referralRowKey) : []);
    };

    const handleRefundRowSelect = (key) => {
        setSelectedRefundKeys((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };
    const handleSelectAllRefund = (event) => {
        setSelectedRefundKeys(event.target.checked ? refundTabRows.map(refundRowKey) : []);
    };
    const handleProcessSelectedReferral = () => {
        if (selectedReferralKeys.length === 0) {
            props.setSnackInfo("Please select at least one row to proceed", "warning");
            return;
        }
        const rows = referralTabRows.filter((row) => selectedReferralKeys.includes(referralRowKey(row)));
        const mapped = rows.map((row, index) => ({
            serialNo: index + 1,
            paymentType: `Referral - ${row.paymentType === 'kickOff' ? 'Kick Off' : 'Office Meet'}`,
            vendorName: row.payeeName,
            accountHolder: row.bank?.accountHolder || row.payeeName,
            orderId: row.uniqueLeadNumber,
            OrderNumber: row.uniqueLeadNumber,
            AllPaymentId: generateUniquePaymentId('DPREFP'),
            approvedPaymentIds: `${row.referralId}_${row.paymentType}`,
            accountNumber: row.bank?.accountNumber,
            date: row.approvedDate || moment().format("DD/MM/YYYY"),
            amount: row.amount,
            ifsc: row.bank?.ifscCode,
            bankName: row.bank?.bankName,
            email: row.email || "",
            referralId: row.referralId,
            referralPaymentType: row.paymentType,
        }));

        setSyncType('referral');
        setSelectedPaymentData(mapped);
        setDkModalOpen(true);
    };

    const handleProcessSelectedRefund = () => {
        if (selectedRefundKeys.length === 0) {
            props.setSnackInfo("Please select at least one row to proceed", "warning");
            return;
        }
        const rows = refundTabRows.filter((row) => selectedRefundKeys.includes(refundRowKey(row)));
        const mapped = rows.map((row, index) => ({
            serialNo: index + 1,
            paymentType: "Refund",
            // Payee is the same lead's customer being refunded.
            vendorName: row.payeeName || row.customerName,
            accountHolder: row.bank?.accountHolder || row.payeeName || row.customerName,
            orderId: row.uniqueLeadNumber,
            OrderNumber: row.uniqueLeadNumber,
            AllPaymentId: generateUniquePaymentId('DPRFDP'),
            approvedPaymentIds: `${row.leadId}_${row.refundEntryId}`,
            accountNumber: row.bank?.accountNumber,           
            date: moment(row.refundRaiseDate).format("DD/MM/YYYY"),
            amount: row.amount,
            ifsc: row.bank?.ifscCode,
            bankName: row.bank?.bankName,
            email: row.email || "",
            leadId: row.leadId,
            refundEntryId: row.refundEntryId,
        }));

        setSyncType('refund');
        setSelectedPaymentData(mapped);
        setDkModalOpen(true);
    };

    // ── Site Visit tab row selection helpers ────────────────────
    const siteVisitRowKey = (row) => `${row.referralId}`;

    const handleSiteVisitRowSelect = (key) => {
        setSelectedSiteVisitKeys((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };
    const handleSelectAllSiteVisit = (event) => {
        setSelectedSiteVisitKeys(event.target.checked ? siteVisitTabRows.map(siteVisitRowKey) : []);
    };

    const handleProcessSelectedSiteVisit = () => {
        if (selectedSiteVisitKeys.length === 0) {
            props.setSnackInfo("Please select at least one row to proceed", "warning");
            return;
        }
        const rows = siteVisitTabRows.filter((row) => selectedSiteVisitKeys.includes(siteVisitRowKey(row)));
        const mapped = rows.map((row, index) => ({
            serialNo: index + 1,
            paymentType: "Site Visit",
            // Payee is the referrer (property holder) who hosted the visit, not the source lead.
            vendorName: row.payeeName || row.referrerName,
            accountHolder: row.bank?.accountHolder || row.payeeName || row.referrerName,
            orderId: row.referrerUniqueLeadNumber || row.uniqueLeadNumber,
            OrderNumber: row.referrerUniqueLeadNumber || row.uniqueLeadNumber,
            AllPaymentId: generateUniquePaymentId('DPSVP'),
            approvedPaymentIds: `${row.referralId}_physicalSiteVisit`,
            accountNumber: row.bank?.accountNumber,
            date: row.approvedDate || moment().format("DD/MM/YYYY"),
            amount: row.amount,
            ifsc: row.bank?.ifscCode,
            bankName: row.bank?.bankName,
            email: row.email || "",
            referralId: row.referralId,
        }));

        setSyncType('siteVisit');
        setSelectedPaymentData(mapped);
        setDkModalOpen(true);
    };

    // ── Channel Partner tab row selection helpers ──────────────────────
    const channelPartnerRowKey = (row) => `${row.payoutId}`;

    const handleChannelPartnerRowSelect = (key) => {
        setSelectedChannelPartnerKeys((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };
    const handleSelectAllChannelPartner = (event) => {
        setSelectedChannelPartnerKeys(event.target.checked ? channelPartnerTabRows.map(channelPartnerRowKey) : []);
    };

    const handleProcessSelectedChannelPartner = () => {
        if (selectedChannelPartnerKeys.length === 0) {
            props.setSnackInfo("Please select at least one row to proceed", "warning");
            return;
        }
        const rows = channelPartnerTabRows.filter((row) => selectedChannelPartnerKeys.includes(channelPartnerRowKey(row)));
        const mapped = rows.map((row, index) => ({
            serialNo: index + 1,
            paymentType: "Channel Partner",
            // Payee sent to the bank must be the account holder on file, not the
            // Channel Partner's personal/display name.
            vendorName: row.payeeName || row.channelPartnerName,
            accountHolder: row.bank?.accountHolder || row.payeeName,
            orderId: row.cpCode || row.uniqueLeadNumber,
            OrderNumber: row.cpCode || row.uniqueLeadNumber,
            AllPaymentId: generateUniquePaymentId('DPCPP'),
            approvedPaymentIds: `${row.payoutId}`,
            accountNumber: row.bank?.accountNumber,
            date: moment().format("DD/MM/YYYY"),
            amount: row.amount,
            ifsc: row.bank?.ifscCode,
            bankName: row.bank?.bankName,
            email: row.email || "",
            payoutId: row.payoutId,
        }));

        setSyncType('channelPartner');
        setSelectedPaymentData(mapped);
        setDkModalOpen(true);
    };

    // ─── Pagination ──────────────────────────────────────────────────────────
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const newLimit = parseInt(event.target.value, 10);
        setRowsPerPage(newLimit);
        setPage(0);
    };

    // ─── Payment Summary ────────────────────────────────────────────────────
    const getPaymentSummary = () => {
        const totalAmount = paymentData.reduce((sum, payment) => {
            return sum + parseFloat(payment.amount || 0);
        }, 0);

        const uniqueVendors = new Set(
            paymentData.map((payment) => payment.vendorName)
        ).size;

        return {
            totalAmount,
            totalRecords: paymentData.length,
            uniqueVendors
        };
    };

    const paymentSummary = getPaymentSummary();

    // ─── Effects ─────────────────────────────────────────────────────────────
    useEffect(() => {
        if (activeTab !== 'misc_ec') return;
        generateVendorPaymentSnorkelHDFC(page, rowsPerPage);
    }, [activeTab, page, rowsPerPage, paymentTypeFilter, experienceCenterFilter]);

    useEffect(() => {
        if (activeTab !== 'referral') return;
        fetchReferralTabData(tabPage, tabRowsPerPage);
    }, [activeTab, tabPage, tabRowsPerPage, experienceCenterFilter]);

    useEffect(() => {
        if (activeTab !== 'refund') return;
        fetchRefundTabData(tabPage, tabRowsPerPage);
    }, [activeTab, tabPage, tabRowsPerPage, experienceCenterFilter]);

    useEffect(() => {
        if (activeTab !== 'siteVisit') return;
        fetchSiteVisitTabData(tabPage, tabRowsPerPage);
    }, [activeTab, tabPage, tabRowsPerPage]);

    useEffect(() => {
        if (activeTab !== 'channelPartner') return;
        fetchChannelPartnerTabData(tabPage, tabRowsPerPage);
    }, [activeTab, tabPage, tabRowsPerPage]);

    // Reset tab-local pagination whenever the user switches tabs.
    const handleTabChange = (event, newTab) => {
        setActiveTab(newTab);
        setTabPage(0);
    };

    useEffect(() => {
        const cooldownEndTime = localStorage.getItem('uploadCooldownEnd');
        if (cooldownEndTime) {
            const remaining = parseInt(cooldownEndTime) - Date.now();
            if (remaining > 0) {
                const remainingSeconds = Math.ceil(remaining / 1000);
                setCooldownTimeLeft(remainingSeconds);
                setUploadCooldown(true);
            } else {
                localStorage.removeItem('uploadCooldownEnd');
            }
        }
    }, []);

    // ─── Loading State ──────────────────────────────────────────────────────
    if (loadingg) {
        return (
            <Typography variant="h6" style={{ marginTop: '16px', color: '#666' }}>
                Loading Please Wait....
            </Typography>
        );
    }

    // ─── Client-side multi-select filters (Vendor / City / Scope) over the
    // currently loaded page of rows — narrows the table display only. ──────
    const uniqueVendors = [...new Set(paymentData.map((r) => r.vendorName).filter(Boolean))];
    const uniqueCities = [...new Set(paymentData.map((r) => r.city).filter(Boolean))];
    const uniqueScopes = [...new Set(paymentData.map((r) => r.paymentType).filter(Boolean))];
    const visibleRows = paymentData.filter((r) =>
        (vendorFilter.length === 0 || vendorFilter.includes(r.vendorName)) &&
        (cityFilter.length === 0 || cityFilter.includes(r.city)) &&
        (scopeFilter.length === 0 || scopeFilter.includes(r.paymentType))
    );

    // payment rail chip (same look as the SO / PO pages): I = HDFC internal, N = NEFT, R = RTGS
    const bankTypeMeta = {
        I: { label: "HDFC internal transfer", bg: "#dbeafe", color: "#1d4ed8" },
        N: { label: "NEFT", bg: "#dcfce7", color: "#15803d" },
        R: { label: "RTGS", bg: "#fef3c7", color: "#b45309" },
    };
    const renderBankType = (value) => {
        if (!value) return displayOrDash(value);
        const meta = bankTypeMeta[value] || { label: value, bg: "#e2e8f0", color: "#334155" };
        return (
            <Tooltip title={meta.label} arrow>
                <Chip label={value} size="small" style={{ backgroundColor: meta.bg, color: meta.color, fontWeight: 700, minWidth: 28 }} />
            </Tooltip>
        );
    };
    const nameCellStyle = { fontWeight: 700, color: "#0f172a" };
    const leadCellStyle = { fontFamily: 'monospace', fontSize: "0.8rem", fontWeight: 700, color: "#1d4ed8" };

    // ₹ lives in the column header only; amount columns get their own header colour
    const amountColumns = {
        'Amount': { label: 'Amount (₹)', bg: '#2563eb' },
        'Revised Amount': { label: 'Revised Amount (₹)', bg: '#b45309' },
        'Approved Amount': { label: 'Approved Amount (₹)', bg: '#0f766e' },
        'Paid Amount': { label: 'Paid Amount (₹)', bg: '#059669' },
    };

    // ─── Main Render ────────────────────────────────────────────────────────
    return (
        <div className={classes.root}>
            {/* Page header + actions */}
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 12, marginBottom: 20 }}>
            <Box>
                <Typography variant="h5" style={{ fontWeight: 800, color: "#0f172a" }}>
                    Miscellaneous Payments
                </Typography>
                <Typography variant="body2" style={{ color: "#64748b" }}>
                    EC, referral, refund, site visit and channel-partner payouts — review, send to the bank and sync.
                </Typography>
            </Box>
            <Grid container justifyContent="flex-end" alignItems="center" spacing={1} wrap="nowrap" style={{ width: "auto", margin: 0 }}>
                <Grid item>
                    <Button
                        variant="outlined"
                        onClick={(e) => setSyncTypeMenuAnchor(e.currentTarget)}
                        startIcon={<SyncIcon />}
                        style={{
                            height: 40,
                            fontSize: 13,
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 10,
                            borderColor: "#2563eb",
                            color: "#2563eb",
                            background: "#fff"
                        }}
                    >
                        Sync Payments
                    </Button>
                    <Menu
                        anchorEl={syncTypeMenuAnchor}
                        open={Boolean(syncTypeMenuAnchor)}
                        onClose={() => setSyncTypeMenuAnchor(null)}
                    >
                        <MenuItem onClick={() => openSyncModal('ec_payment')}>EC Payment</MenuItem>
                        <MenuItem onClick={() => openSyncModal('referral')}>Referral</MenuItem>
                        <MenuItem onClick={() => openSyncModal('refund')}>Refund</MenuItem>
                        <MenuItem onClick={() => openSyncModal('siteVisit')}>Site Visit</MenuItem>
                        <MenuItem onClick={() => openSyncModal('channelPartner')}>Channel Partner</MenuItem>
                    </Menu>
                </Grid>

                <Grid item>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        style={{ height: 40, fontSize: 13, textTransform: "none", fontWeight: 600, borderRadius: 10, backgroundColor: "#0f172a", color: "#fff" }}
                    >
                        <CSVLink
                            data={
                                activeTab === 'referral'
                                    ? referralTabRows.map((row, i) => ({
                                        serialNo: i + 1,
                                        bankTxnType: row.bankTxnType,
                                        uniqueLeadNumber: row.uniqueLeadNumber,
                                        referedBy: row.referedBy,
                                        referrerUniqueLeadNumber: row.referrerUniqueLeadNumber,
                                        referedTo: row.referedTo,
                                        referalType: row.referalType,
                                        stage: row.stage,
                                        paymentType: row.paymentType === 'kickOff' ? 'Kick Off' : 'Office Meet',
                                        amount: row.amount,
                                        approvedDate: row.approvedDate,
                                        accountNumber: row.bank?.accountNumber,
                                        payeeName: row.payeeName,
                                        ifsc: row.bank?.ifscCode,
                                        bankName: row.bank?.bankName,
                                        email: row.email,
                                    }))
                                    : activeTab === 'refund'
                                        ? refundTabRows.map((row, i) => ({
                                            serialNo: i + 1,
                                            bankTxnType: row.bankTxnType,
                                            uniqueLeadNumber: row.uniqueLeadNumber,
                                            customerName: row.customerName,
                                            amount: row.amount,
                                            refundRaiseDate: row.refundRaiseDate ? moment(row.refundRaiseDate).format("DD/MM/YYYY") : "",
                                            accountNumber: row.bank?.accountNumber,
                                            payeeName: row.payeeName,
                                            ifsc: row.bank?.ifscCode,
                                            bankName: row.bank?.bankName,
                                            email: row.email,
                                        }))
                                        : activeTab === 'siteVisit'
                                            ? siteVisitTabRows.map((row, i) => ({
                                                serialNo: i + 1,
                                                bankTxnType: row.bankTxnType,
                                                uniqueLeadNumber: row.uniqueLeadNumber,
                                                referrerUniqueLeadNumber: row.referrerUniqueLeadNumber,
                                                referrerName: row.referrerName,
                                                amount: row.amount,
                                                approvedDate: row.approvedDate,
                                                accountNumber: row.bank?.accountNumber,
                                                payeeName: row.payeeName,
                                                ifsc: row.bank?.ifscCode,
                                                bankName: row.bank?.bankName,
                                                email: row.email,
                                            }))
                                            : activeTab === 'channelPartner'
                                                ? channelPartnerTabRows.map((row, i) => ({
                                                    serialNo: i + 1,
                                                    bankTxnType: row.bankTxnType,
                                                    payoutCode: row.payoutCode,
                                                    cpCode: row.cpCode,
                                                    channelPartnerName: row.channelPartnerName,
                                                    uniqueLeadNumber: row.uniqueLeadNumber,
                                                    amount: row.amount,
                                                    approvedDate: row.approvedDate,
                                                    accountNumber: row.bank?.accountNumber,
                                                    payeeName: row.payeeName,
                                                    ifsc: row.bank?.ifscCode,
                                                    bankName: row.bank?.bankName,
                                                    email: row.email,
                                                }))
                                                : (paymentData || [])
                            }
                            headers={activeTab === 'referral' ? referralCsvHeaders : activeTab === 'refund' ? refundCsvHeaders : activeTab === 'siteVisit' ? siteVisitCsvHeaders : activeTab === 'channelPartner' ? channelPartnerCsvHeaders : csvHeaders}
                            filename={activeTab === 'referral' ? "ReferralPayments.csv" : activeTab === 'refund' ? "RefundPayments.csv" : activeTab === 'siteVisit' ? "PhysicalSiteVisitPayments.csv" : activeTab === 'channelPartner' ? "ChannelPartnerPayments.csv" : "VendorPayments.csv"}
                            style={{ textDecoration: "none", color: "inherit" }}
                        >
                            Download CSV
                        </CSVLink>
                    </Button>
                </Grid>
            </Grid>
            </Box>

            <Container maxWidth="xl" style={{ paddingTop: 24 }}>

                {/* ─── Payment Type Tabs ─────────────────────────────────────── */}
                <Paper elevation={0} style={{ marginBottom: 20, borderRadius: 14, border: "1px solid #e2e8f0", boxShadow: "0 4px 20px rgba(15,23,42,0.07)" }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        <Tab label="Misc / EC Payments" value="misc_ec" style={{ textTransform: "none", fontWeight: 700 }} />
                        <Tab label="Referral" value="referral" style={{ textTransform: "none", fontWeight: 700 }} />
                        <Tab label="Refund" value="refund" style={{ textTransform: "none", fontWeight: 700 }} />
                        <Tab label="Site Visit" value="siteVisit" style={{ textTransform: "none", fontWeight: 700 }} />
                        <Tab label="Channel Partner" value="channelPartner" style={{ textTransform: "none", fontWeight: 700 }} />
                    </Tabs>
                </Paper>

                {/* ─── Filter Section ────────────────────────────────────────── */}
                <Paper className={classes.filterContainer}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={2}>
                            <TextField
                                label="From Date"
                                type="date"
                                name="startDate"
                                value={dateFilter.startDate}
                                onChange={handleDateChange}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={2}>
                            <TextField
                                label="To Date"
                                type="date"
                                name="endDate"
                                value={dateFilter.endDate}
                                onChange={handleDateChange}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        </Grid>

                        {activeTab === 'misc_ec' && (
                            <Grid item xs={12} sm={2}>
                                <FormControl fullWidth variant="outlined" size="small">
                                    <InputLabel>Payment Type</InputLabel>
                                    <Select
                                        value={paymentTypeFilter}
                                        onChange={(e) => {
                                            setPaymentTypeFilter(e.target.value);
                                            setPage(0);
                                        }}
                                        label="Payment Type"
                                    >
                                        <MenuItem value="all">All Types</MenuItem>
                                        {Object.entries(PAYMENT_TYPE_LABELS).map(([key, label]) => (
                                            <MenuItem key={key} value={key}>{label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        <Grid item xs={12} sm={2}>
                            <FormControl fullWidth variant="outlined" size="small">
                                <InputLabel>Experience Centre</InputLabel>
                                <Select
                                    value={experienceCenterFilter}
                                    onChange={(e) => {
                                        setExperienceCenterFilter(e.target.value);
                                        setPage(0);
                                    }}
                                    label="Experience Centre"
                                >
                                    <MenuItem value="all">All Centers</MenuItem>
                                    {allExperienceCenters.map((ec) => (
                                        <MenuItem key={ec._id} value={ec._id}>{ec.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {activeTab === 'misc_ec' && (
                            <>
                                <Grid item xs={12} sm={2}>
                                    <FormControl fullWidth variant="outlined" size="small">
                                        <InputLabel>Vendor</InputLabel>
                                        <Select
                                            multiple
                                            value={vendorFilter}
                                            onChange={(e) => setVendorFilter(e.target.value)}
                                            label="Vendor"
                                            renderValue={(selected) => selected.length ? `${selected.length} selected` : "All"}
                                        >
                                            {uniqueVendors.map((v) => (
                                                <MenuItem key={v} value={v}>
                                                    <Checkbox checked={vendorFilter.includes(v)} size="small" />
                                                    {v}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={2}>
                                    <FormControl fullWidth variant="outlined" size="small">
                                        <InputLabel>City</InputLabel>
                                        <Select
                                            multiple
                                            value={cityFilter}
                                            onChange={(e) => setCityFilter(e.target.value)}
                                            label="City"
                                            renderValue={(selected) => selected.length ? `${selected.length} selected` : "All"}
                                        >
                                            {uniqueCities.map((c) => (
                                                <MenuItem key={c} value={c}>
                                                    <Checkbox checked={cityFilter.includes(c)} size="small" />
                                                    {c}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={2}>
                                    <FormControl fullWidth variant="outlined" size="small">
                                        <InputLabel>Scope / Expense Type</InputLabel>
                                        <Select
                                            multiple
                                            value={scopeFilter}
                                            onChange={(e) => setScopeFilter(e.target.value)}
                                            label="Scope / Expense Type"
                                            renderValue={(selected) => selected.length ? `${selected.length} selected` : "All"}
                                        >
                                            {uniqueScopes.map((s) => (
                                                <MenuItem key={s} value={s}>
                                                    <Checkbox checked={scopeFilter.includes(s)} size="small" />
                                                    {PAYMENT_TYPE_LABELS[s] || s}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12} sm={2}>
                            <Box display="flex" gap={1}>
                                <Button
                                    variant="contained"
                                    onClick={handleDateFilter}
                                    className={classes.filterButton}
                                >
                                    Filter
                                </Button>

                                <Button
                                    variant="contained"
                                    onClick={handleDateFilterClear}
                                    className={classes.clearButton}
                                >
                                    Clear
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

                {activeTab === 'misc_ec' && <>
                {/* ─── Summary Cards ────────────────────────────────────────── */}
                <Grid container spacing={2} style={{ marginBottom: 24 }}>
                    {[
                        { label: "Total Amount", value: `₹${Number(paymentSummary.totalAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, color: "#2563eb", bg: "#eff6ff", Icon: PaymentIcon },
                        { label: "Total Records", value: paymentSummary.totalRecords, color: "#059669", bg: "#ecfdf5", Icon: CheckCircleIcon },
                        { label: "Unique Vendors", value: paymentSummary.uniqueVendors, color: "#7c3aed", bg: "#f5f3ff", Icon: BusinessIcon },
                    ].map(({ label, value, color, bg, Icon }) => (
                        <Grid item xs={12} sm={4} key={label}>
                            <Card className={classes.summaryCard} style={{ borderLeft: `5px solid ${color}` }}>
                                <CardContent style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <Box>
                                        <Typography className={classes.statLabel} style={{ fontWeight: 600 }}>
                                            {label}
                                        </Typography>
                                        <Typography style={{ fontSize: "1.5rem", fontWeight: 800, color }}>
                                            {value}
                                        </Typography>
                                    </Box>
                                    <Box style={{ background: bg, color, borderRadius: 12, padding: 10, display: "flex" }}>
                                        <Icon />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* ─── Table Section ────────────────────────────────────────── */}
                <Card style={{ borderRadius: 14, boxShadow: "0 4px 20px rgba(15,23,42,0.07)", border: "1px solid #e2e8f0" }}>
                    <CardContent>
                        <TableContainer component={Paper} elevation={0} className={classes.tableWrap} style={{ maxHeight: 600 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            style={{
                                                backgroundColor: "#1e293b",
                                                color: "#fff",
                                                fontWeight: 600,
                                                fontSize: "0.85rem"
                                            }}
                                        >
                                            <Checkbox
                                                checked={selectedRows.length === paymentData.length && paymentData.length > 0}
                                                indeterminate={selectedRows.length > 0 && selectedRows.length < paymentData.length}
                                                onChange={handleSelectAll}
                                                style={{ color: "#fff" }}
                                            />
                                        </TableCell>
                                        {[
                                            'SL No', 'Type', 'Scope', 'Vendor Name', 'Order ID',
                                            'Account No', 'Date', 'Payment Date', 'Amount',
                                            'Revised Amount', 'Approved  Date ', 'Approved Amount', 'Paid Amount',
                                            'Account Holder', 'IFSC', 'Bank', 'Email', 'Actions',
                                        ].map((header, idx) => (
                                            <TableCell
                                                key={idx}
                                                align={amountColumns[header] ? "right" : "left"}
                                                style={{
                                                    backgroundColor: amountColumns[header]?.bg || "#1e293b",
                                                    color: "#fff",
                                                    fontWeight: 600,
                                                    fontSize: "0.85rem"
                                                }}
                                            >
                                                {amountColumns[header]?.label || header}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {visibleRows.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={19} align="center" style={{ padding: 40 }}>
                                                <Typography variant="body1" color="textSecondary">
                                                    No data available
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        visibleRows.map((row, index) => (
                                            <TableRow key={index} className={classes.tableRow}>
                                                <TableCell>
                                                    <Checkbox
                                                        checked={selectedRows.includes(row.serialNo)}
                                                        onChange={() => handleRowSelect(row.serialNo)}
                                                    />
                                                </TableCell>
                                                <TableCell>{row.serialNo}</TableCell>
                                                <TableCell>{renderBankType(row.bankTxnType)}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={row.paymentType}
                                                        size="small"
                                                        style={{
                                                            backgroundColor: "#e0f2fe",
                                                            color: "#0369a1",
                                                            fontWeight: 600,
                                                            fontSize: "0.7rem"
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell style={nameCellStyle}>{row.vendorName}</TableCell>
                                                <TableCell style={{ fontFamily: 'monospace', fontSize: "0.75rem" }}>
                                                    {row.orderId}
                                                </TableCell>
                                                <TableCell style={{ fontFamily: 'monospace', fontSize: "0.75rem" }}>
                                                    {row.accountNumber}
                                                </TableCell>
                                                <TableCell>{row.date}</TableCell>
                                                <TableCell>{todayDate}</TableCell>
                                                <TableCell align="right">
                                                    <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontWeight: 700, fontVariantNumeric: "tabular-nums", background: "#eff6ff", color: "#1d4ed8" }}>
                                                        {Number(row.amount).toLocaleString(undefined, {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2
                                                        })}
                                                    </span>
                                                    <Tooltip title="Last 3 months">
                                                        <IconButton size="small" onClick={(e) => handleShowAmountHistory(e, row)}>
                                                            <HistoryIcon style={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row.revisedAmount != null
                                                        ? <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontWeight: 700, fontVariantNumeric: "tabular-nums", background: "#fffbeb", color: "#b45309" }}>{Number(row.revisedAmount).toLocaleString()}</span>
                                                        : "—"}
                                                </TableCell>
                                                <TableCell>
                                                    {displayOrDash(row.lastApprovedDate)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontWeight: 700, fontVariantNumeric: "tabular-nums", background: "#f0fdfa", color: "#0f766e" }}>{Number(row.approvedAmount).toLocaleString()}</span>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontWeight: 700, fontVariantNumeric: "tabular-nums", background: "#ecfdf5", color: "#047857" }}>{Number(row.paidAmount).toLocaleString()}</span>
                                                    {row.revisionType === "postponed" && (
                                                        <Chip size="small" label="Postponed" style={{ marginLeft: 4, fontSize: "0.65rem" }} />
                                                    )}
                                                    {row.revisionType === "penalty" && (
                                                        <Chip size="small" label="Penalty" style={{ marginLeft: 4, fontSize: "0.65rem", backgroundColor: "#fee2e2", color: "#dc2626" }} />
                                                    )}
                                                </TableCell>
                                                <TableCell style={nameCellStyle}>{displayOrDash(row.accountHolder)}</TableCell>
                                                <TableCell style={{ fontFamily: 'monospace', fontSize: "0.7rem" }}>
                                                    {displayOrDash(row.ifsc)}
                                                </TableCell>
                                                <TableCell>{displayOrDash(row.bankName)}</TableCell>
                                                <TableCell>{displayOrDash(row.email)}</TableCell>
                                                <TableCell>
                                                    <Tooltip title={row.sentToBank ? "Already sent to bank — cannot edit" : "Edit amount before payment"}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={row.sentToBank}
                                                                onClick={() => handleOpenReviseDialog(row)}
                                                            >
                                                                <EditIcon style={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* ─── Action Button ───────────────────────────────── */}
                        <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="textSecondary">
                                {selectedRows.length} of {paymentData.length} rows selected
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={handleProceedDK}
                                disabled={selectedRows.length <= 0 || approvalLoading || uploadCooldown}
                                className={classes.processButton}
                                startIcon={approvalLoading ? <Oval size={20} color="inherit" /> : <SendIcon />}
                            >
                                {approvalLoading
                                    ? "Processing..."
                                    : uploadCooldown
                                        ? `Wait ${formatTimeRemaining(cooldownTimeLeft)}`
                                        : `Process Selected (${selectedRows.length})`
                                }
                            </Button>
                        </Box>

                        {/* ─── Pagination ───────────────────────────────────── */}
                        <TablePagination
                            component="div"
                            count={totalDocs}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[100, 200, 300, 500]}
                            labelRowsPerPage="Rows per page:"
                        />
                    </CardContent>
                </Card>
                </>}

                {activeTab === 'referral' && (
                    <Card style={{ borderRadius: 14, boxShadow: "0 4px 20px rgba(15,23,42,0.07)", border: "1px solid #e2e8f0" }}>
                        <CardContent>
                            <TableContainer component={Paper} elevation={0} className={classes.tableWrap} style={{ maxHeight: 600 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ backgroundColor: "#1e293b", color: "#fff" }}>
                                                <Checkbox
                                                    checked={selectedReferralKeys.length === referralTabRows.length && referralTabRows.length > 0}
                                                    indeterminate={selectedReferralKeys.length > 0 && selectedReferralKeys.length < referralTabRows.length}
                                                    onChange={handleSelectAllReferral}
                                                    style={{ color: "#fff" }}
                                                />
                                            </TableCell>
                                            {[
                                                'SL No', 'Type', 'Unq Lead No', 'Referred By', 'Referred By Unq Lead No', 'Referred To',
                                                'Referral Type', 'Stage', 'Payment Type', 'Amount', 'Approved Date', 'Payment Date',
                                                'Account No', 'Account Holder', 'IFSC', 'Bank', 'Email',
                                            ].map((header) => (
                                                <TableCell
                                                    key={header}
                                                    align={amountColumns[header] ? "right" : "left"}
                                                    style={{ backgroundColor: amountColumns[header]?.bg || "#1e293b", color: "#fff", fontWeight: 600, fontSize: "0.85rem" }}
                                                >
                                                    {amountColumns[header]?.label || header}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {referralTabLoading ? (
                                            <TableRow>
                                                <TableCell colSpan={18} align="center" style={{ padding: 40 }}>
                                                    <CircularProgress size={24} />
                                                </TableCell>
                                            </TableRow>
                                        ) : referralTabRows.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={18} align="center" style={{ padding: 40 }}>
                                                    <Typography variant="body1" color="textSecondary">
                                                        No pending referral payments
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            referralTabRows.map((row, index) => {
                                                const key = referralRowKey(row);
                                                return (
                                                    <TableRow key={key} className={classes.tableRow}>
                                                        <TableCell>
                                                            <Checkbox
                                                                checked={selectedReferralKeys.includes(key)}
                                                                onChange={() => handleReferralRowSelect(key)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{tabPage * tabRowsPerPage + index + 1}</TableCell>
                                                        <TableCell>{renderBankType(row.bankTxnType)}</TableCell>
                                                        <TableCell style={leadCellStyle}>{displayOrDash(row.uniqueLeadNumber)}</TableCell>
                                                        <TableCell style={nameCellStyle}>{displayOrDash(row.referedBy)}</TableCell>
                                                        <TableCell style={leadCellStyle}>{displayOrDash(row?.referrerUniqueLeadNumber)}</TableCell>
                                                        <TableCell style={nameCellStyle}>{displayOrDash(row.referedTo)}</TableCell>
                                                        <TableCell>{displayOrDash(row.referalType)}</TableCell>
                                                        <TableCell>{displayOrDash(row.stage)}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={row.paymentType === 'kickOff' ? 'Kick Off' : 'Office Meet'}
                                                                size="small"
                                                                style={{ backgroundColor: "#e0f2fe", color: "#0369a1", fontWeight: 600, fontSize: "0.7rem" }}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontWeight: 700, fontVariantNumeric: "tabular-nums", background: "#eff6ff", color: "#1d4ed8" }}>
                                                                {Number(row.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell>{displayOrDash(row.approvedDate)}</TableCell>
                                                        <TableCell>{todayDate}</TableCell>
                                                        <TableCell style={{ fontFamily: 'monospace', fontSize: "0.7rem" }}>{displayOrDash(row.bank?.accountNumber)}</TableCell>
                                                        <TableCell style={nameCellStyle}>{displayOrDash(row.payeeName)}</TableCell>
                                                        <TableCell style={{ fontFamily: 'monospace', fontSize: "0.7rem" }}>{displayOrDash(row.bank?.ifscCode)}</TableCell>
                                                        <TableCell>{displayOrDash(row.bank?.bankName)}</TableCell>
                                                        <TableCell>{displayOrDash(row.email)}</TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" color="textSecondary">
                                    {selectedReferralKeys.length} of {referralTabRows.length} rows selected
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={handleProcessSelectedReferral}
                                    disabled={selectedReferralKeys.length === 0 || approvalLoading || uploadCooldown}
                                    className={classes.processButton}
                                    startIcon={approvalLoading && syncType === 'referral' ? <Oval size={20} color="inherit" /> : <SendIcon />}
                                >
                                    {approvalLoading && syncType === 'referral'
                                        ? "Processing..."
                                        : uploadCooldown
                                            ? `Wait ${formatTimeRemaining(cooldownTimeLeft)}`
                                            : `Process Selected (${selectedReferralKeys.length})`
                                    }
                                </Button>
                            </Box>

                            <TablePagination
                                component="div"
                                count={referralTabTotal}
                                page={tabPage}
                                onPageChange={(e, newPage) => setTabPage(newPage)}
                                rowsPerPage={tabRowsPerPage}
                                onRowsPerPageChange={(e) => { setTabRowsPerPage(parseInt(e.target.value, 10)); setTabPage(0); }}
                                rowsPerPageOptions={[100, 200, 300, 500]}
                                labelRowsPerPage="Rows per page:"
                            />
                        </CardContent>
                    </Card>
                )}

                {activeTab === 'refund' && (
                    <Card style={{ borderRadius: 14, boxShadow: "0 4px 20px rgba(15,23,42,0.07)", border: "1px solid #e2e8f0" }}>
                        <CardContent>
                            <TableContainer component={Paper} elevation={0} className={classes.tableWrap} style={{ maxHeight: 600 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ backgroundColor: "#1e293b", color: "#fff" }}>
                                                <Checkbox
                                                    checked={selectedRefundKeys.length === refundTabRows.length && refundTabRows.length > 0}
                                                    indeterminate={selectedRefundKeys.length > 0 && selectedRefundKeys.length < refundTabRows.length}
                                                    onChange={handleSelectAllRefund}
                                                    style={{ color: "#fff" }}
                                                />
                                            </TableCell>
                                            {[
                                                'SL No', 'Type', 'Unq Lead No', 'Customer Name', 'Amount',
                                                'Refund Raised Date', 'Payment Date', 'Account No', 'Account Holder', 'IFSC', 'Bank', 'Email',
                                            ].map((header) => (
                                                <TableCell
                                                    key={header}
                                                    align={amountColumns[header] ? "right" : "left"}
                                                    style={{ backgroundColor: amountColumns[header]?.bg || "#1e293b", color: "#fff", fontWeight: 600, fontSize: "0.85rem" }}
                                                >
                                                    {amountColumns[header]?.label || header}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {refundTabLoading ? (
                                            <TableRow>
                                                <TableCell colSpan={13} align="center" style={{ padding: 40 }}>
                                                    <CircularProgress size={24} />
                                                </TableCell>
                                            </TableRow>
                                        ) : refundTabRows.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={13} align="center" style={{ padding: 40 }}>
                                                    <Typography variant="body1" color="textSecondary">
                                                        No pending refund payments
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            refundTabRows.map((row, index) => {
                                                const key = refundRowKey(row);
                                                return (
                                                    <TableRow key={key} className={classes.tableRow}>
                                                        <TableCell>
                                                            <Checkbox
                                                                checked={selectedRefundKeys.includes(key)}
                                                                onChange={() => handleRefundRowSelect(key)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{tabPage * tabRowsPerPage + index + 1}</TableCell>
                                                        <TableCell>{renderBankType(row.bankTxnType)}</TableCell>
                                                        <TableCell style={leadCellStyle}>{displayOrDash(row.uniqueLeadNumber)}</TableCell>
                                                        <TableCell style={nameCellStyle}>{displayOrDash(row.customerName)}</TableCell>
                                                        <TableCell align="right">
                                                            <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontWeight: 700, fontVariantNumeric: "tabular-nums", background: "#eff6ff", color: "#1d4ed8" }}>
                                                                {Number(row.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell>{row.refundRaiseDate ? moment(row.refundRaiseDate).format("DD/MM/YYYY") : "-"}</TableCell>
                                                        <TableCell>{todayDate}</TableCell>
                                                        <TableCell style={{ fontFamily: 'monospace', fontSize: "0.7rem" }}>{displayOrDash(row.bank?.accountNumber)}</TableCell>
                                                        <TableCell style={nameCellStyle}>{displayOrDash(row.payeeName)}</TableCell>
                                                        <TableCell style={{ fontFamily: 'monospace', fontSize: "0.7rem" }}>{displayOrDash(row.bank?.ifscCode)}</TableCell>
                                                        <TableCell>{displayOrDash(row.bank?.bankName)}</TableCell>
                                                        <TableCell>{displayOrDash(row.email)}</TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" color="textSecondary">
                                    {selectedRefundKeys.length} of {refundTabRows.length} rows selected
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={handleProcessSelectedRefund}
                                    disabled={selectedRefundKeys.length === 0 || approvalLoading || uploadCooldown}
                                    className={classes.processButton}
                                    startIcon={approvalLoading && syncType === 'refund' ? <Oval size={20} color="inherit" /> : <SendIcon />}
                                >
                                    {approvalLoading && syncType === 'refund'
                                        ? "Processing..."
                                        : uploadCooldown
                                            ? `Wait ${formatTimeRemaining(cooldownTimeLeft)}`
                                            : `Process Selected (${selectedRefundKeys.length})`
                                    }
                                </Button>
                            </Box>

                            <TablePagination
                                component="div"
                                count={refundTabTotal}
                                page={tabPage}
                                onPageChange={(e, newPage) => setTabPage(newPage)}
                                rowsPerPage={tabRowsPerPage}
                                onRowsPerPageChange={(e) => { setTabRowsPerPage(parseInt(e.target.value, 10)); setTabPage(0); }}
                                rowsPerPageOptions={[100, 200, 300, 500]}
                                labelRowsPerPage="Rows per page:"
                            />
                        </CardContent>
                    </Card>
                )}

                {activeTab === 'siteVisit' && (
                    <Card style={{ borderRadius: 14, boxShadow: "0 4px 20px rgba(15,23,42,0.07)", border: "1px solid #e2e8f0" }}>
                        <CardContent>
                            <TableContainer component={Paper} elevation={0} className={classes.tableWrap} style={{ maxHeight: 600 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ backgroundColor: "#1e293b", color: "#fff" }}>
                                                <Checkbox
                                                    checked={selectedSiteVisitKeys.length === siteVisitTabRows.length && siteVisitTabRows.length > 0}
                                                    indeterminate={selectedSiteVisitKeys.length > 0 && selectedSiteVisitKeys.length < siteVisitTabRows.length}
                                                    onChange={handleSelectAllSiteVisit}
                                                    style={{ color: "#fff" }}
                                                />
                                            </TableCell>
                                            {[
                                                'SL No', 'Type', 'Source Lead No', 'Referrer Lead No', 'Referrer Name',
                                                'Amount', 'Approved Date', 'Payment Date', 'Account No', 'Account Holder', 'IFSC', 'Bank', 'Email',
                                            ].map((header) => (
                                                <TableCell
                                                    key={header}
                                                    align={amountColumns[header] ? "right" : "left"}
                                                    style={{ backgroundColor: amountColumns[header]?.bg || "#1e293b", color: "#fff", fontWeight: 600, fontSize: "0.85rem" }}
                                                >
                                                    {amountColumns[header]?.label || header}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {siteVisitTabLoading ? (
                                            <TableRow>
                                                <TableCell colSpan={15} align="center" style={{ padding: 40 }}>
                                                    <CircularProgress size={24} />
                                                </TableCell>
                                            </TableRow>
                                        ) : siteVisitTabRows.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={15} align="center" style={{ padding: 40 }}>
                                                    <Typography variant="body1" color="textSecondary">
                                                        No pending site visit payments
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            siteVisitTabRows.map((row, index) => {
                                                const key = siteVisitRowKey(row);
                                                return (
                                                    <TableRow key={key} className={classes.tableRow}>
                                                        <TableCell>
                                                            <Checkbox
                                                                checked={selectedSiteVisitKeys.includes(key)}
                                                                onChange={() => handleSiteVisitRowSelect(key)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{tabPage * tabRowsPerPage + index + 1}</TableCell>
                                                        <TableCell>{renderBankType(row.bankTxnType)}</TableCell>
                                                        <TableCell style={leadCellStyle}>{displayOrDash(row.uniqueLeadNumber)}</TableCell>
                                                        <TableCell style={leadCellStyle}>{displayOrDash(row.referrerUniqueLeadNumber)}</TableCell>
                                                        <TableCell style={nameCellStyle}>{displayOrDash(row.referrerName)}</TableCell>
                                                        <TableCell align="right">
                                                            <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontWeight: 700, fontVariantNumeric: "tabular-nums", background: "#eff6ff", color: "#1d4ed8" }}>
                                                                {Number(row.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell>{displayOrDash(row.approvedDate)}</TableCell>
                                                        <TableCell>{todayDate}</TableCell>
                                                        <TableCell style={{ fontFamily: 'monospace', fontSize: "0.7rem" }}>{displayOrDash(row.bank?.accountNumber)}</TableCell>
                                                        <TableCell style={nameCellStyle}>{displayOrDash(row.payeeName)}</TableCell>
                                                        <TableCell style={{ fontFamily: 'monospace', fontSize: "0.7rem" }}>{displayOrDash(row.bank?.ifscCode)}</TableCell>
                                                        <TableCell>{displayOrDash(row.bank?.bankName)}</TableCell>
                                                        <TableCell>{displayOrDash(row.email)}</TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" color="textSecondary">
                                    {selectedSiteVisitKeys.length} of {siteVisitTabRows.length} rows selected
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={handleProcessSelectedSiteVisit}
                                    disabled={selectedSiteVisitKeys.length === 0 || approvalLoading || uploadCooldown}
                                    className={classes.processButton}
                                    startIcon={approvalLoading && syncType === 'siteVisit' ? <Oval size={20} color="inherit" /> : <SendIcon />}
                                >
                                    {approvalLoading && syncType === 'siteVisit'
                                        ? "Processing..."
                                        : uploadCooldown
                                            ? `Wait ${formatTimeRemaining(cooldownTimeLeft)}`
                                            : `Process Selected (${selectedSiteVisitKeys.length})`
                                    }
                                </Button>
                            </Box>

                            <TablePagination
                                component="div"
                                count={siteVisitTabTotal}
                                page={tabPage}
                                onPageChange={(e, newPage) => setTabPage(newPage)}
                                rowsPerPage={tabRowsPerPage}
                                onRowsPerPageChange={(e) => { setTabRowsPerPage(parseInt(e.target.value, 10)); setTabPage(0); }}
                                rowsPerPageOptions={[100, 200, 300, 500]}
                                labelRowsPerPage="Rows per page:"
                            />
                        </CardContent>
                    </Card>
                )}

                {activeTab === 'channelPartner' && (
                    <Card style={{ borderRadius: 14, boxShadow: "0 4px 20px rgba(15,23,42,0.07)", border: "1px solid #e2e8f0" }}>
                        <CardContent>
                            <TableContainer component={Paper} elevation={0} className={classes.tableWrap} style={{ maxHeight: 600 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ backgroundColor: "#1e293b", color: "#fff" }}>
                                                <Checkbox
                                                    checked={selectedChannelPartnerKeys.length === channelPartnerTabRows.length && channelPartnerTabRows.length > 0}
                                                    indeterminate={selectedChannelPartnerKeys.length > 0 && selectedChannelPartnerKeys.length < channelPartnerTabRows.length}
                                                    onChange={handleSelectAllChannelPartner}
                                                    style={{ color: "#fff" }}
                                                />
                                            </TableCell>
                                            {[
                                                'SL No', 'Type', 'Payout Code', 'CP Code', 'Channel Partner', 'Lead No',
                                                'Amount', 'Approved Date', 'Payment Date', 'Account No', 'Account Holder', 'IFSC', 'Bank', 'Email',
                                            ].map((header) => (
                                                <TableCell
                                                    key={header}
                                                    align={amountColumns[header] ? "right" : "left"}
                                                    style={{ backgroundColor: amountColumns[header]?.bg || "#1e293b", color: "#fff", fontWeight: 600, fontSize: "0.85rem" }}
                                                >
                                                    {amountColumns[header]?.label || header}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {channelPartnerTabLoading ? (
                                            <TableRow>
                                                <TableCell colSpan={15} align="center" style={{ padding: 40 }}>
                                                    <CircularProgress size={24} />
                                                </TableCell>
                                            </TableRow>
                                        ) : channelPartnerTabRows.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={15} align="center" style={{ padding: 40 }}>
                                                    <Typography variant="body1" color="textSecondary">
                                                        No pending Channel Partner payouts
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            channelPartnerTabRows.map((row, index) => {
                                                const key = channelPartnerRowKey(row);
                                                return (
                                                    <TableRow key={key} className={classes.tableRow}>
                                                        <TableCell>
                                                            <Checkbox
                                                                checked={selectedChannelPartnerKeys.includes(key)}
                                                                onChange={() => handleChannelPartnerRowSelect(key)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{tabPage * tabRowsPerPage + index + 1}</TableCell>
                                                        <TableCell>{renderBankType(row.bankTxnType)}</TableCell>
                                                        <TableCell style={{ fontFamily: 'monospace', fontSize: "0.75rem" }}>{displayOrDash(row.payoutCode)}</TableCell>
                                                        <TableCell style={{ fontFamily: 'monospace', fontSize: "0.75rem" }}>{displayOrDash(row.cpCode)}</TableCell>
                                                        <TableCell style={nameCellStyle}>{displayOrDash(row.channelPartnerName)}</TableCell>
                                                        <TableCell style={leadCellStyle}>{displayOrDash(row.uniqueLeadNumber)}</TableCell>
                                                        <TableCell align="right">
                                                            <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontWeight: 700, fontVariantNumeric: "tabular-nums", background: "#eff6ff", color: "#1d4ed8" }}>
                                                                {Number(row.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell>{displayOrDash(row.approvedDate)}</TableCell>
                                                        <TableCell>{todayDate}</TableCell>
                                                        <TableCell style={{ fontFamily: 'monospace', fontSize: "0.7rem" }}>{displayOrDash(row.bank?.accountNumber)}</TableCell>
                                                        <TableCell style={nameCellStyle}>{displayOrDash(row.payeeName)}</TableCell>
                                                        <TableCell style={{ fontFamily: 'monospace', fontSize: "0.7rem" }}>{displayOrDash(row.bank?.ifscCode)}</TableCell>
                                                        <TableCell>{displayOrDash(row.bank?.bankName)}</TableCell>
                                                        <TableCell>{displayOrDash(row.email)}</TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" color="textSecondary">
                                    {selectedChannelPartnerKeys.length} of {channelPartnerTabRows.length} rows selected
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={handleProcessSelectedChannelPartner}
                                    disabled={selectedChannelPartnerKeys.length === 0 || approvalLoading || uploadCooldown}
                                    className={classes.processButton}
                                    startIcon={approvalLoading && syncType === 'channelPartner' ? <Oval size={20} color="inherit" /> : <SendIcon />}
                                >
                                    {approvalLoading && syncType === 'channelPartner'
                                        ? "Processing..."
                                        : uploadCooldown
                                            ? `Wait ${formatTimeRemaining(cooldownTimeLeft)}`
                                            : `Process Selected (${selectedChannelPartnerKeys.length})`
                                    }
                                </Button>
                            </Box>

                            <TablePagination
                                component="div"
                                count={channelPartnerTabTotal}
                                page={tabPage}
                                onPageChange={(e, newPage) => setTabPage(newPage)}
                                rowsPerPage={tabRowsPerPage}
                                onRowsPerPageChange={(e) => { setTabRowsPerPage(parseInt(e.target.value, 10)); setTabPage(0); }}
                                rowsPerPageOptions={[100, 200, 300, 500]}
                                labelRowsPerPage="Rows per page:"
                            />
                        </CardContent>
                    </Card>
                )}
            </Container>

            {/* ─── Sync Modal ───────────────────────────────────────────────── */}
            <Dialog
                open={syncModalOpen}
                onClose={() => setSyncModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ style: { borderRadius: 16 } }}
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                            <SyncIcon style={{ color: "#2563eb" }} />
                            <Typography variant="h6" style={{ fontWeight: 700 }}>
                                Sync {syncType === 'referral' ? 'Referral' : syncType === 'refund' ? 'Refund' : syncType === 'siteVisit' ? 'Site Visit' : syncType === 'channelPartner' ? 'Channel Partner' : 'EC'} Payments to ERP
                            </Typography>
                        </Box>
                        <IconButton onClick={() => setSyncModalOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                        Select a past Snorkel upload and run sync to mark those payments as synced in ERP.
                    </Typography>
                </DialogTitle>

                <DialogContent dividers>
                    {syncLogsLoading ? (
                        <Box display="flex" alignItems="center" justifyContent="center" py={4} style={{ gap: 12 }}>
                            <Oval color="#2563eb" height={24} width={24} />
                            <Typography color="textSecondary">Loading upload history...</Typography>
                        </Box>
                    ) : snorkelLogs.length === 0 ? (
                        <Box display="flex" alignItems="center" justifyContent="center" py={4}
                            style={{ border: "1px dashed #cbd5e1", borderRadius: 10 }}>
                            <Typography color="textSecondary" variant="body2">
                                No upload logs found. Logs appear here after a successful Snorkel upload.
                            </Typography>
                        </Box>
                    ) : (
                        <Box style={{ maxHeight: 320, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
                            {snorkelLogs.map((log, idx) => {
                                const isSelected = selectedSyncLog?._id === log._id;
                                return (
                                    <Box
                                        key={log._id || idx}
                                        onClick={() => setSelectedSyncLog(isSelected ? null : log)}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            padding: "12px 16px",
                                            borderRadius: 10,
                                            border: `2px solid ${isSelected ? "#2563eb" : "#e2e8f0"}`,
                                            background: isSelected ? "#eff6ff" : "#f8fafc",
                                            cursor: "pointer",
                                            transition: "all 0.18s",
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                                            <Box style={{
                                                width: 16, height: 16, borderRadius: "50%",
                                                border: `2px solid ${isSelected ? "#2563eb" : "#94a3b8"}`,
                                                background: isSelected ? "#2563eb" : "transparent",
                                                flexShrink: 0, transition: "all 0.2s",
                                            }} />
                                            <Box>
                                                <Typography variant="body2" style={{ fontWeight: 600, color: "#1e293b" }}>
                                                    Upload #{snorkelLogs.length - idx}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {moment(log.uploadedAt).format("DD MMM YYYY, hh:mm A")} {" | "}
                                                    Uploaded by : {log.uploadedBy?.name ? ` ${log.uploadedBy.name}` : ""}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                            <Chip
                                                label={log.syncedAt ? "Synced" : "Pending"}
                                                size="small"
                                                style={{
                                                    fontWeight: 600, fontSize: "0.75rem",
                                                    backgroundColor: log.syncedAt ? "#dcfce7" : "#fef9c3",
                                                    color: log.syncedAt ? "#16a34a" : "#a16207",
                                                }}
                                            />
                                            <Tooltip title={log.s3Location ? "Download uploaded file" : "No file available"}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        component="a"
                                                        href={log.s3Location}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        download
                                                        disabled={!log.s3Location}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <DownloadIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}

                    {selectedSyncLog && (
                        <Box mt={2} p={1.5} style={{ background: "#eff6ff", borderRadius: 8, border: "1px solid #bfdbfe" }}>
                            <Typography variant="body2" style={{ color: "#1d4ed8" }}>
                                ✅ Upload from <strong>{moment(selectedSyncLog.uploadedAt).format("DD MMM YYYY, hh:mm A")}</strong> selected.
                                Clicking <strong>Run Sync</strong> will mark all matching orders as synced in ERP.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions style={{ padding: "16px 24px", gap: 8 }}>
                    <Button
                        onClick={fetchSnorkelLogs}
                        disabled={syncLogsLoading}
                        startIcon={<SyncIcon style={{ fontSize: 16 }} />}
                        style={{ textTransform: "none", color: "#64748b" }}
                    >
                        {syncLogsLoading ? "Refreshing..." : "Refresh"}
                    </Button>
                    <Box flex={1} />
                    <Button onClick={() => setSyncModalOpen(false)} style={{ textTransform: "none" }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRunSync}
                        disabled={!selectedSyncLog || syncRunning}
                        variant="contained"
                        startIcon={syncRunning ? <Oval color="#fff" height={16} width={16} /> : <CloudUploadIcon />}
                        style={{
                            backgroundColor: (!selectedSyncLog || syncRunning) ? "#e2e8f0" : "#2563eb",
                            color: (!selectedSyncLog || syncRunning) ? "#94a3b8" : "#fff",
                            textTransform: "none", fontWeight: 600, borderRadius: 8,
                        }}
                    >
                        {syncRunning ? "Syncing..." : "Run Sync"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ─── Sync Result Modal ────────────────────────────────────────── */}
            <Dialog
                open={syncResultModal}
                onClose={() => setSyncResultModal(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ style: { borderRadius: 16 } }}
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" style={{ fontWeight: 700 }}>Sync Complete</Typography>
                        <IconButton onClick={() => setSyncResultModal(false)} size="small"><CloseIcon /></IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {syncResult && (
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Box style={{ background: "#dcfce7", borderRadius: 12, padding: 16, textAlign: "center" }}>
                                    <Typography style={{ color: "#16a34a", fontSize: "2rem", fontWeight: 700 }}>
                                        {syncResult.updatedCount ?? "—"}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">Orders Updated</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box style={{ background: "#fef9c3", borderRadius: 12, padding: 16, textAlign: "center" }}>
                                    <Typography style={{ color: "#a16207", fontSize: "2rem", fontWeight: 700 }}>
                                        {syncResult.skippedCount ?? "—"}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">Skipped / Not Found</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                                    {syncResult.message || "Matching payments have been marked in ERP."}
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: "16px 24px" }}>
                    <Button
                        onClick={() => { setSyncResultModal(false); setSyncModalOpen(false); }}
                        variant="contained"
                        color="primary"
                        style={{ textTransform: "none", borderRadius: 8 }}
                    >
                        Done
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ─── OTP Verification Modal ───────────────────────────────────── */}
            {dkModalOpen && (
                <Dialog
                    open={dkModalOpen}
                    onClose={() => {
                        setDkModalOpen(false);
                        setOtpResponse([]);
                        setOtpSend(false);
                        setOtpVerified(false);
                    }}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        style: {
                            borderRadius: 20,
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                            overflow: "hidden",
                        },
                    }}
                >
                    <DialogTitle style={{ padding: 0 }}>
                        <Box
                            style={{
                                background: "rgba(255, 255, 255, 0.1)",
                                backdropFilter: "blur(10px)",
                                padding: "24px",
                                color: "white",
                            }}
                        >
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box display="flex" alignItems="center">
                                    <Box
                                        style={{
                                            background: "rgba(255, 255, 255, 0.2)",
                                            borderRadius: "50%",
                                            padding: "12px",
                                            marginRight: "16px",
                                        }}
                                    >
                                        <EmailIcon style={{ fontSize: 28, color: "white" }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 4 }}>
                                            OTP Verification
                                        </Typography>
                                        <Typography variant="body2" style={{ opacity: 0.9 }}>
                                            Verify your identity to proceed
                                        </Typography>
                                    </Box>
                                </Box>
                                <IconButton
                                    onClick={() => {
                                        setDkModalOpen(false);
                                        setOtpResponse([]);
                                        setOtpSend(false);
                                        setOtpVerified(false);
                                    }}
                                    style={{ color: "rgba(255, 255, 255, 0.8)" }}
                                    disabled={approvalLoading}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </Box>
                    </DialogTitle>

                    <DialogContent style={{ padding: "32px", background: "white" }}>
                        <Box display="flex" justifyContent="center" marginBottom={3}>
                            <Box
                                style={{
                                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                    borderRadius: "50%",
                                    padding: "20px",
                                }}
                            >
                                <EmailIcon style={{ fontSize: 40, color: "white" }} />
                            </Box>
                        </Box>

                        {!otpSend ? (
                            // Send OTP Section
                            <Box textAlign="center">
                                <Typography variant="h6" style={{ marginBottom: 24, color: "#1e293b", fontWeight: 600 }}>
                                    Send Verification Code
                                </Typography>
                                <Box
                                    style={{
                                        background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                                        padding: "20px",
                                        borderRadius: 12,
                                        marginBottom: 24,
                                    }}
                                >
                                    <Typography variant="body1" style={{ color: "#64748b", marginBottom: 16 }}>
                                        We'll send a verification code to:
                                    </Typography>
                                    <Typography variant="body1" style={{ color: "#1e293b", fontWeight: 600 }}>
                                        📧 {isDK?.email}
                                    </Typography>
                                </Box>
                                <Button
                                    onClick={setOTPMessage}
                                    variant="contained"
                                    disabled={loading || approvalLoading}
                                    startIcon={loading ? <Oval color="white" height={20} width={20} /> : <SendIcon />}
                                    style={{
                                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                        color: "white",
                                        fontWeight: 700,
                                        borderRadius: 12,
                                        textTransform: "none",
                                        padding: "12px 32px",
                                        fontSize: "16px",
                                        boxShadow: "0 10px 15px -3px rgba(103, 126, 234, 0.4)",
                                        width: "100%",
                                    }}
                                >
                                    {loading ? "Sending..." : "Send OTP"}
                                </Button>
                            </Box>
                        ) : (
                            // Verify OTP Section
                            <Box>
                                <Typography
                                    variant="h6"
                                    align="center"
                                    style={{ marginBottom: 24, color: "#1e293b", fontWeight: 600 }}
                                >
                                    Enter Verification Code
                                </Typography>
                                <TextField
                                    id="outlined-basic1-otp"
                                    label="Enter 6-digit OTP"
                                    variant="outlined"
                                    fullWidth
                                    onChange={verifyOTP}
                                    disabled={approvalLoading}
                                    inputProps={{
                                        maxLength: 6,
                                        style: {
                                            fontSize: "18px",
                                            textAlign: "center",
                                            letterSpacing: "0.5em",
                                        },
                                    }}
                                    style={{ marginBottom: 24 }}
                                    InputProps={{
                                        style: { borderRadius: 12, background: "#f8fafc" },
                                    }}
                                    InputLabelProps={{
                                        style: { color: "#64748b" },
                                    }}
                                />

                                {otpVerified && !approvalLoading && (
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        style={{
                                            background: "rgba(34, 197, 94, 0.1)",
                                            color: "#059669",
                                            padding: "12px",
                                            borderRadius: 12,
                                            marginBottom: 16,
                                        }}
                                    >
                                        <CheckCircleIcon style={{ marginRight: 8, fontSize: 20 }} />
                                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                                            OTP Verified Successfully!
                                        </Typography>
                                    </Box>
                                )}

                                {approvalLoading && (
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        style={{
                                            background: "rgba(59, 130, 246, 0.1)",
                                            color: "#2563eb",
                                            padding: "12px",
                                            borderRadius: 12,
                                            marginBottom: 16,
                                        }}
                                    >
                                        <Oval color="#2563eb" height={20} width={20} style={{ marginRight: 8 }} />
                                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                                            Processing approval request...
                                        </Typography>
                                    </Box>
                                )}

                                <Box
                                    style={{
                                        background: "rgba(103, 126, 234, 0.1)",
                                        borderRadius: 12,
                                        padding: "16px",
                                        border: "1px solid rgba(103, 126, 234, 0.2)",
                                    }}
                                >
                                    <Typography variant="body2" style={{ color: "#475569", textAlign: "center" }}>
                                        💡 Check your E-Mail for the 6-digit verification code
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </DialogContent>

                    <DialogActions style={{ padding: "24px", background: "#f8fafc" }}>
                        {otpSend && (
                            <Button
                                onClick={async () => {
                                    if (otpVerified && !approvalLoading) {
                                        try {
                                            await FinalApprovalFlowByDKFunction();
                                            setDkModalOpen(false);
                                            setOtpResponse([]);
                                            setOtpVerified(false);
                                            setOtpSend(false);
                                            setModalOpen(false);
                                        } catch (error) {
                                            console.error("Error in final approval flow:", error);
                                        }
                                    }
                                }}
                                variant="contained"
                                disabled={!otpVerified || approvalLoading}
                                startIcon={approvalLoading ? <Oval color="white" height={20} width={20} /> : <SendIcon />}
                                style={{
                                    background: (otpVerified && !approvalLoading)
                                        ? "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
                                        : "#e2e8f0",
                                    color: (otpVerified && !approvalLoading) ? "white" : "#94a3b8",
                                    fontWeight: 700,
                                    borderRadius: 12,
                                    textTransform: "none",
                                    padding: "12px 32px",
                                    boxShadow: (otpVerified && !approvalLoading)
                                        ? "0 10px 15px -3px rgba(34, 197, 94, 0.4)"
                                        : "none",
                                }}
                            >
                                {approvalLoading ? "Processing..." : "Proceed"}
                            </Button>
                        )}
                    </DialogActions>
                </Dialog>
            )}

            {/* ─── Progress Modal ───────────────────────────────────────────── */}
            {showProgressModal && (
                <Dialog
                    open={showProgressModal}
                    onClose={canCloseModal ? () => setShowProgressModal(false) : undefined}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{
                        style: {
                            borderRadius: 20,
                            padding: 24,
                            background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                        }
                    }}
                >
                    <DialogTitle>
                        <Typography variant="h6" fontWeight={700}>
                            Processing Payments
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Box>
                            <LinearProgress
                                variant="determinate"
                                value={processingProgress}
                                style={{
                                    height: 10,
                                    borderRadius: 5,
                                    backgroundColor: "#e2e8f0",
                                }}
                            />
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                align="center"
                                style={{ marginTop: 8, marginBottom: 4 }}
                            >
                                {processingProgress.toFixed(0)}% Complete
                            </Typography>
                            <Typography
                                variant="body2"
                                align="center"
                                style={{ marginBottom: 8, color: "#334155" }}
                            >
                                {processingMessage}
                            </Typography>
                            {!canCloseModal && (
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    align="center"
                                    display="block"
                                    style={{ fontStyle: "italic" }}
                                >
                                    Please don't close this window or press back. Processing will complete automatically.
                                </Typography>
                            )}
                            {canCloseModal && (
                                <Button
                                    onClick={() => setShowProgressModal(false)}
                                    variant="contained"
                                    fullWidth
                                    style={{
                                        background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                                        color: "white",
                                        fontWeight: 700,
                                        marginTop: 24,
                                        borderRadius: 12,
                                        textTransform: "none",
                                    }}
                                >
                                    Close
                                </Button>
                            )}
                        </Box>
                    </DialogContent>
                </Dialog>
            )}

            {/* ── Edit Amount dialog ────────────────────────────────────────── */}
            <Dialog open={reviseDialogOpen} onClose={() => setReviseDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: 16 } }}>
                <DialogTitle style={{ background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)", color: "#fff" }}>
                    <Typography variant="h6" style={{ fontWeight: 800 }}>Edit Amount</Typography>
                    <Typography variant="body2" style={{ color: "#cbd5e1" }}>
                        <strong>{reviseRow?.vendorName}</strong>
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Revised Amount"
                        type="number"
                        value={reviseAmount}
                        onChange={(e) => setReviseAmount(e.target.value)}
                        InputProps={{ startAdornment: "₹", style: { color: "#2563eb", fontWeight: 700, fontSize: "1.05rem" } }}
                        InputLabelProps={{ style: { fontWeight: 700 } }}
                        FormHelperTextProps={{ style: { fontWeight: 700, color: "#059669" } }}
                        helperText={`Originally raised: ₹${Number(reviseRow?.amount || 0).toLocaleString()}`}
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Revision Type</InputLabel>
                        <Select value={reviseType} onChange={(e) => setReviseType(e.target.value)} label="Revision Type">
                            <MenuItem value="none">None</MenuItem>
                            <MenuItem value="postponed">Postponed Payment</MenuItem>
                            <MenuItem value="penalty">Penalty Payment</MenuItem>
                        </Select>
                    </FormControl>
                    {reviseType === "postponed" && (
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Postponed Note"
                            multiline
                            rows={2}
                            placeholder="e.g. ₹5,000 to be added to next month's payment"
                            value={revisePostponedNote}
                            onChange={(e) => setRevisePostponedNote(e.target.value)}
                        />
                    )}
                    {reviseType === "penalty" && (
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Penalty Amount"
                            type="number"
                            value={revisePenaltyAmount}
                            onChange={(e) => setRevisePenaltyAmount(e.target.value)}
                            InputProps={{ startAdornment: "₹", style: { color: "#dc2626", fontWeight: 700 } }}
                            FormHelperTextProps={{ style: { fontWeight: 700, color: "#b45309" } }}
                            helperText="Deducted from Paid Amount this cycle — not carried forward"
                        />
                    )}
                </DialogContent>
                <DialogActions style={{ padding: "12px 24px", background: "#f8fafc" }}>
                    <Button onClick={() => setReviseDialogOpen(false)} disabled={revising} style={{ textTransform: "none" }}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmitRevise}
                        disabled={revising}
                        style={{ textTransform: "none", fontWeight: 700, borderRadius: 8 }}
                    >
                        {revising ? "Saving…" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Last 3 months' amount history popover ────────────────────── */}
            <Popover
                open={Boolean(historyAnchorEl)}
                anchorEl={historyAnchorEl}
                onClose={() => setHistoryAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Box p={2} minWidth={220}>
                    <Typography variant="caption" style={{ fontWeight: 600, display: "block", marginBottom: 8 }}>
                        Last 3 Months
                    </Typography>
                    {historyLoading ? (
                        <CircularProgress size={16} />
                    ) : historyRows.length === 0 ? (
                        <Typography variant="caption" color="textSecondary">No prior payments found</Typography>
                    ) : (
                        historyRows.map((h) => (
                            <Typography key={h._id} variant="caption" color="textSecondary" display="block">
                                {h.payment_period_label || h.payment_month || new Date(h.created_at).toLocaleDateString()}: ₹{h.amount?.toLocaleString()}
                            </Typography>
                        ))
                    )}
                </Box>
            </Popover>
        </div>
    );
};

export default connect(null, { setLoading, setSnackInfo })(MiscpaymentMasterFinanceDashboard);